import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { messagesService, parseCreateThreadError, } from "@/api/services/messages.service";
import { sitesService } from "@/api/services/sites.service";
import { quotesService } from "@/api/services/quotes.service";
import { additionalWorksService } from "@/api/services/additional-works.service";
import { jobsService } from "@/api/services/jobs.service";
import { contactsService } from "@/api/services/contacts.service";
import { formsService } from "@/api/services/forms.service";
import { documentsService, } from "@/api/services/documents.service";
import { CommencementDateInput, DateInput, StarRating, } from "@/components/common";
import { Modal } from "@/components/common/Modal";
import { ActionRequiredBadge } from "./ActionRequiredBadge";
import { ChangeDateModal } from "./ChangeDateModal";
import { TradeDateChangeRequestModal } from "./TradeDateChangeRequestModal";
import { BuildingPermitModal } from "./BuildingPermitModal";
import { JobChecklist } from "./JobChecklist";
import checklistStyles from "./JobChecklist.module.css";
import { JobQA } from "./JobQA";
import { NO_COMPANY_SENTINEL, useGhlTradeContacts, } from "../hooks/useGhlTradeContacts";
import { GhlTradeContactSelect } from "./GhlTradeContactSelect";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/common/Toast";
import { useAlert } from "@/hooks/useAlert";
import { formatFullName, getContactDisplayName } from "@/utils/string";
import { useShoppingCentreDisplay } from "@/hooks/useShoppingCentreDisplay";
import { formatSiteAddress } from "@/utils/address";
import { getDelayDays, getTimeFromIso, getTodayAUS, isWeekend, nowAUSISO, parseDateTime, toDateOnly, toDateTime, toDateTimeLocal, } from "@/utils/dateTime";
import { TRADE_TYPE_LABELS } from "@/types/job.types";
import { BUILDING_PERMIT_DESCRIPTIONS, getBuildingPermitSlotsFromDocuments, } from "@/utils/buildingPermitDocuments";
import { getAdditionalInformationFromNotes } from "@/utils/buildingPermitNotes";
import { selectCurrentBuildingPermitForm } from "@/utils/buildingPermitFormUtils";
import { buildFullTradeAssignmentPayload } from "@/utils/tradeAssignmentUtils";
import styles from "./JobDetailTab.module.css";
// Helper to convert display building type to enum format
const toEnumBuildingType = (displayType) => {
    return displayType.toUpperCase().replace(/\s+/g, "_");
};
function getSurveyorDisplayLabel(contact) {
    return contact.companyName
        ? `${contact.companyName} - ${contact.name}`
        : contact.name;
}
// Encumbered trade types that require external contractors
const ENCUMBERED_TRADE_TYPES = [
    "fire_services_wet",
    "electrical",
    "fire_services_dry",
    "hydraulics",
    "tiling",
];
// DEFIT internal trade types
const DEFIT_TRADE_TYPES = [
    "defit",
    "floor_grinding",
    "plasterer",
    "painting",
    "carpentry",
];
const ALL_TRADE_TYPES = Object.keys(TRADE_TYPE_LABELS);
const LABEL_AND_VALUE_TO_TRADE_TYPE = (() => {
    const m = new Map();
    for (const type of ALL_TRADE_TYPES) {
        m.set(type, type);
        const label = TRADE_TYPE_LABELS[type];
        if (label)
            m.set(label.toLowerCase().trim(), type);
    }
    return m;
})();
/** Map API tradeType (value or label, any casing) to canonical TradeType for grouping. */
function canonicalTradeType(apiValue) {
    const s = (apiValue || "").trim();
    if (!s)
        return null;
    const fromMap = LABEL_AND_VALUE_TO_TRADE_TYPE.get(s) ??
        LABEL_AND_VALUE_TO_TRADE_TYPE.get(s.toLowerCase());
    if (fromMap)
        return fromMap;
    const normalized = s.toLowerCase().replace(/\s+/g, "_");
    return ALL_TRADE_TYPES.includes(normalized)
        ? normalized
        : null;
}
export const JobDetailTab = ({ jobId, job, jobLoading: loading, jobSaving: saving, jobError: error, refetchJob: refetch, updateJob, updateTradeAssignment, availableSites, sitesLoading, allContacts, refetchContacts, surveyorContacts, siteDocuments: siteDocumentsFromProps, jobDocuments: jobDocumentsFromProps, jobDocumentsFromDocumentsApi = [], quoteDocuments: quoteDocumentsFromProps = [], refetchSiteDocuments, refetchJobDocumentsFromDocumentsApi, refetchQuoteDocuments, checklistItems, checklistLoading: checklistLoadingFromProps, refetchChecklist, onOpenAddDocument, tradeDateChangeRequests: tradeDateChangeRequestsFromProps, refetchTradeDateChangeRequests, hasDilapidation = false, }) => {
    const { user, isStaff, isAdmin } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const basePath = location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    const jobChecklistRef = useRef(null);
    const canAddChecklistItem = isAdmin || isStaff;
    const toast = useToast();
    const { showAlert } = useAlert();
    const [tradeTab, setTradeTab] = useState("encumbered");
    const [isChangeDateModalOpen, setIsChangeDateModalOpen] = useState(false);
    const [tradeDateChangeRequestAssignment, setTradeDateChangeRequestAssignment,] = useState(null);
    const [isTradeDateChangeModalOpen, setIsTradeDateChangeModalOpen] = useState(false);
    const [isBuildingPermitModalOpen, setIsBuildingPermitModalOpen] = useState(false);
    const [openBuildingPermitForEdit, setOpenBuildingPermitForEdit] = useState(false);
    const [isStaffDateChangeModalOpen, setIsStaffDateChangeModalOpen] = useState(false);
    const [staffDateChangeData, setStaffDateChangeData] = useState(null);
    const [staffDateChangeReason, setStaffDateChangeReason] = useState("");
    const [cfiRequested, setCfiRequested] = useState(false);
    const [buildingPermitRequested, setBuildingPermitRequested] = useState(false);
    // Track original job dates to detect changes
    const [originalJobDates, setOriginalJobDates] = useState(null);
    const isInitialEmptyJobDates = (original) => !!original &&
        original.durationDays === 0 &&
        !original.commencementDate?.trim() &&
        !original.completionDate?.trim();
    const [siteData, setSiteData] = useState(null);
    const [siteLoading, setSiteLoading] = useState(false);
    const [selectedSiteId, setSelectedSiteId] = useState("");
    const [jobNameValue, setJobNameValue] = useState("");
    // Derive building permit docs from site, job (documents API), quote, and job (jobs API)
    const buildingPermitDocs = useMemo(() => getBuildingPermitSlotsFromDocuments(siteDocumentsFromProps, jobDocumentsFromDocumentsApi, jobDocumentsFromProps, quoteDocumentsFromProps), [
        siteDocumentsFromProps,
        jobDocumentsFromDocumentsApi,
        jobDocumentsFromProps,
        quoteDocumentsFromProps,
    ]);
    const additionalInformationFromNotes = useMemo(() => getAdditionalInformationFromNotes(job?.notes), [job?.notes]);
    // Assigned Contact from job's contactId (Contact entity, not site's contact person)
    const assignedContact = useMemo(() => {
        if (!job?.contactId)
            return null;
        return allContacts.find((c) => c.id === job.contactId) ?? null;
    }, [job?.contactId, allContacts]);
    const assignedCompanyName = assignedContact?.companyName ?? null;
    const assignedContactName = assignedContact?.name ?? null;
    // Company-first contact selection (searchable dropdowns like AddJob)
    const companyDropdownRef = useRef(null);
    const contactDropdownRef = useRef(null);
    const [companyNames, setCompanyNames] = useState([]);
    const [loadingCompanyNames, setLoadingCompanyNames] = useState(false);
    const [companySearchTerm, setCompanySearchTerm] = useState("");
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [selectedCompanyName, setSelectedCompanyName] = useState("");
    const [companyContacts, setCompanyContacts] = useState([]);
    const [contactSearchTerm, setContactSearchTerm] = useState("");
    const [showContactDropdown, setShowContactDropdown] = useState(false);
    const [selectedContactDisplayName, setSelectedContactDisplayName] = useState("");
    const [contactSaving, setContactSaving] = useState(false);
    // Sync dropdown state from assigned contact.
    // When job.contactId exists but assignedContact is null (e.g. during refetch after save),
    // do NOT overwrite with empty - preserve the user's selection until contacts resolve.
    useEffect(() => {
        setSelectedCompanyName(assignedCompanyName ?? "");
        setCompanySearchTerm(assignedCompanyName ?? "");
        if (assignedContact) {
            setSelectedContactDisplayName(assignedContactName ?? "");
            setContactSearchTerm(assignedContactName ?? "");
        }
        else if (!job?.contactId) {
            setSelectedContactDisplayName("");
            setContactSearchTerm("");
        }
    }, [
        assignedCompanyName,
        assignedContactName,
        assignedContact,
        job?.contactId,
    ]);
    // Fetch company contacts when assigned contact has a company
    useEffect(() => {
        if (!assignedCompanyName?.trim()) {
            setCompanyContacts([]);
            return;
        }
        let cancelled = false;
        contactsService
            .getContacts({ companyName: assignedCompanyName, page: 1 })
            .then((res) => {
            if (cancelled)
                return;
            const contacts = (res.data.data || []).map((contact) => ({
                contactId: contact.contactId,
                firstName: contact.firstName ?? "",
                lastName: contact.lastName ?? "",
                email: contact.email ?? "",
                phone: contact.phone,
                role: contact.role,
            }));
            setCompanyContacts(contacts);
        })
            .catch(() => {
            if (!cancelled)
                setCompanyContacts([]);
        });
        return () => {
            cancelled = true;
        };
    }, [assignedCompanyName]);
    const fetchCompanyNames = useCallback(async () => {
        try {
            setLoadingCompanyNames(true);
            const response = await contactsService.getCompaniesWithContactIds();
            const companiesData = response.data.data || [];
            const companyNameSet = new Set();
            companiesData.forEach((company) => {
                if (company.companyName && company.contactId) {
                    companyNameSet.add(company.companyName);
                }
            });
            const allCompanyNames = Array.from(companyNameSet);
            const filteredNames = companySearchTerm?.trim()
                ? allCompanyNames.filter((name) => name.toLowerCase().includes(companySearchTerm.toLowerCase().trim()))
                : allCompanyNames;
            setCompanyNames(filteredNames);
        }
        catch (err) {
            console.error("Error fetching company names:", err);
            setCompanyNames([]);
        }
        finally {
            setLoadingCompanyNames(false);
        }
    }, [companySearchTerm]);
    const handleCompanySelect = useCallback(async (companyName) => {
        setSelectedCompanyName(companyName);
        setCompanySearchTerm(companyName);
        setShowCompanyDropdown(false);
        setSelectedContactDisplayName("");
        setContactSearchTerm("");
        setShowContactDropdown(false);
        try {
            const contactResponse = await contactsService.getContacts({
                companyName,
                page: 1,
            });
            const contacts = (contactResponse.data.data || []).map((contact) => ({
                contactId: contact.contactId,
                firstName: contact.firstName ?? "",
                lastName: contact.lastName ?? "",
                email: contact.email ?? "",
                phone: contact.phone,
                role: contact.role,
            }));
            setCompanyContacts(contacts);
        }
        catch (err) {
            console.error("Error fetching company contacts:", err);
            setCompanyContacts([]);
        }
    }, []);
    const handleClearCompany = useCallback(() => {
        setSelectedCompanyName("");
        setCompanySearchTerm("");
        setSelectedContactDisplayName("");
        setContactSearchTerm("");
        setShowContactDropdown(false);
        setCompanyContacts([]);
    }, []);
    const handleContactSelect = useCallback(async (contact) => {
        const displayName = getContactDisplayName(contact.firstName, contact.lastName, contact.email);
        setSelectedContactDisplayName(displayName);
        setContactSearchTerm(displayName);
        setShowContactDropdown(false);
        if (!jobId || !updateJob)
            return;
        setContactSaving(true);
        try {
            await updateJob({ contactId: contact.contactId });
            await refetch();
            await refetchContacts?.();
            toast.success("Contact updated");
        }
        catch (err) {
            console.error("Failed to update job contact:", err);
            toast.error("Failed to update contact");
        }
        finally {
            setContactSaving(false);
        }
    }, [jobId, updateJob, refetch, refetchContacts, toast]);
    const handleClearContact = useCallback(() => {
        setSelectedContactDisplayName("");
        setContactSearchTerm("");
        setShowContactDropdown(false);
    }, []);
    const filteredCompanyContacts = useMemo(() => {
        if (!selectedCompanyName)
            return [];
        if (!contactSearchTerm?.trim())
            return companyContacts;
        const searchLower = contactSearchTerm.toLowerCase().trim();
        return companyContacts.filter((contact) => {
            const fullName = formatFullName(contact.firstName, contact.lastName).toLowerCase();
            const email = (contact.email || "").toLowerCase();
            return fullName.includes(searchLower) || email.includes(searchLower);
        });
    }, [companyContacts, contactSearchTerm, selectedCompanyName]);
    // Fetch company names when dropdown is shown
    useEffect(() => {
        if (showCompanyDropdown)
            fetchCompanyNames();
    }, [showCompanyDropdown, fetchCompanyNames]);
    // Click outside and Escape for company dropdown
    useEffect(() => {
        if (!showCompanyDropdown)
            return;
        const container = companyDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && target && !container.contains(target)) {
                setShowCompanyDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape")
                setShowCompanyDropdown(false);
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showCompanyDropdown]);
    // Click outside and Escape for contact dropdown
    useEffect(() => {
        if (!showContactDropdown)
            return;
        const container = contactDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && target && !container.contains(target)) {
                setShowContactDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape")
                setShowContactDropdown(false);
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showContactDropdown]);
    const [finalDocuments] = useState([]); // No API yet; placeholder for Final documents
    const [selectingDocType, setSelectingDocType] = useState(null);
    // Form state for site details
    const [siteForm, setSiteForm] = useState({
        siteName: "",
        address: "",
        suburb: "",
        state: "",
        postcode: "",
        buildingType: "",
        tenancyName: "",
        tenancyNumber: "",
        landlord: "",
    });
    // Form state for job details
    const [jobForm, setJobForm] = useState({
        scope: "",
        description: "",
        durationDays: 0,
        commencementDate: "",
        completionDate: "",
        buildingSurveyorId: "",
        commencementTime: "",
        completionTime: "",
        delayNumber: 0,
        delayReason: "",
        delayReasonOther: "",
        jobSetupCorrectly: false,
        prelimsOnTime: false,
        siteRulesFollowed: false,
        finalQA: null,
    });
    const [resolvedFormId, setResolvedFormId] = useState(null);
    const [linkedQuote, setLinkedQuote] = useState(null);
    // Fetch site data when job changes
    useEffect(() => {
        if (!job?.siteId) {
            setSelectedSiteId("");
            return;
        }
        setSelectedSiteId(job.siteId);
        const fetchSiteData = async () => {
            if (!job.siteId)
                return;
            setSiteLoading(true);
            try {
                const response = await sitesService.getSite(job.siteId);
                setSiteData(response.data);
                setSiteForm({
                    siteName: response.data.siteName || "",
                    address: response.data.address || "",
                    suburb: response.data.suburb || "",
                    state: response.data.state || "",
                    postcode: response.data.postcode || "",
                    buildingType: response.data.buildingTypes?.[0] || "",
                    tenancyName: response.data.tenancyName || "",
                    tenancyNumber: response.data.tenancyNumber || "",
                    landlord: response.data.landlord || "",
                });
            }
            catch (err) {
                console.error("Failed to load site data:", err);
            }
            finally {
                setSiteLoading(false);
            }
        };
        fetchSiteData();
    }, [job?.siteId]);
    useEffect(() => {
        if (!job?.quoteId) {
            setLinkedQuote(null);
            return;
        }
        let cancelled = false;
        quotesService
            .getQuote(job.quoteId)
            .then((res) => {
            if (!cancelled)
                setLinkedQuote(res.data);
        })
            .catch(() => {
            if (!cancelled)
                setLinkedQuote(null);
        });
        return () => {
            cancelled = true;
        };
    }, [job?.quoteId]);
    const siteCentreNestedName = siteData?.shoppingCentre?.scName?.trim();
    const quoteCentreNestedName = linkedQuote?.shoppingCentre?.scName?.trim();
    const siteCentreLine = siteData?.shoppingCentreId ??
        siteData?.shoppingCentre?.id ??
        siteData?.shoppingCenter ??
        siteData?.contactPerson?.shoppingCenter ??
        "";
    const quoteCentreLine = linkedQuote?.shoppingCentreId ?? linkedQuote?.shoppingCentre?.id ?? "";
    const resolvedQuoteCentreName = useShoppingCentreDisplay(quoteCentreLine);
    const siteCentreHookInput = linkedQuote && (quoteCentreNestedName || quoteCentreLine)
        ? ""
        : siteCentreLine;
    const resolvedSiteCentreName = useShoppingCentreDisplay(siteCentreHookInput);
    const shoppingCentreDisplay = useMemo(() => {
        if (quoteCentreNestedName)
            return quoteCentreNestedName;
        if (quoteCentreLine)
            return resolvedQuoteCentreName;
        if (siteCentreNestedName)
            return siteCentreNestedName;
        return resolvedSiteCentreName;
    }, [
        quoteCentreNestedName,
        quoteCentreLine,
        resolvedQuoteCentreName,
        siteCentreNestedName,
        resolvedSiteCentreName,
    ]);
    // Update job form when job data changes
    useEffect(() => {
        if (job) {
            const comm = parseDateTime(job.commencementDate ?? undefined);
            const comp = parseDateTime(job.completionDate ?? undefined);
            const commencementDate = comm.date;
            const completionDate = comp.date;
            const commencementTime = comm.time || job.startTime || "";
            const completionTime = comp.time || job.endTime || "";
            const duration = job.durationDays || 0;
            const rawDelayReason = job.delayReason || "";
            const isPresetReason = rawDelayReason === "Flooring" || rawDelayReason === "Plastering";
            const delayReason = isPresetReason
                ? rawDelayReason
                : rawDelayReason
                    ? "Other"
                    : "";
            const delayReasonOther = rawDelayReason && !isPresetReason ? rawDelayReason : "";
            setJobForm({
                scope: job.scope || "",
                description: job.description || "",
                durationDays: duration,
                commencementDate,
                completionDate,
                buildingSurveyorId: job.buildingSurveyorId || "",
                commencementTime,
                completionTime,
                delayNumber: job.delayNumber || 0,
                delayReason,
                delayReasonOther,
                jobSetupCorrectly: job.jobSetupCorrectly ?? false,
                prelimsOnTime: job.prelimsOnTime ?? false,
                siteRulesFollowed: job.siteRulesFollowed ?? false,
                finalQA: job.finalQA ?? null,
            });
            // Store original dates for comparison
            setOriginalJobDates({
                durationDays: duration,
                commencementDate,
                completionDate,
                commencementTime,
                completionTime,
            });
            // Set request flags
            setCfiRequested(job.cfiRequested || false);
            setBuildingPermitRequested(job.buildingPermitRequested || false);
        }
    }, [job]);
    useEffect(() => {
        setJobNameValue(job?.title ?? "");
    }, [job?.title]);
    useEffect(() => {
        const surveyorId = jobForm.buildingSurveyorId;
        if (!surveyorId) {
            setResolvedFormId(null);
            return;
        }
        const surveyor = surveyorContacts.find((c) => c.id === surveyorId);
        const companyName = surveyor?.companyName?.trim();
        if (!companyName) {
            setResolvedFormId(null);
            return;
        }
        let cancelled = false;
        const resolve = async () => {
            try {
                const res = await formsService.getFormsByCompanyName(companyName, 1, 20);
                if (cancelled)
                    return;
                const forms = res.data?.data ?? [];
                const selected = selectCurrentBuildingPermitForm(forms);
                setResolvedFormId(selected?.id ?? null);
            }
            catch {
                if (cancelled)
                    return;
                setResolvedFormId(null);
            }
        };
        void resolve();
        return () => {
            cancelled = true;
        };
    }, [jobForm.buildingSurveyorId, surveyorContacts]);
    // Handle site selection from dropdown
    const handleSiteSelect = async (siteId) => {
        setSelectedSiteId(siteId);
        if (!siteId) {
            // Clear the form if no site selected
            setSiteForm({
                siteName: "",
                address: "",
                suburb: "",
                state: "",
                postcode: "",
                buildingType: "",
                tenancyName: "",
                tenancyNumber: "",
                landlord: "",
            });
            setSiteData(null);
            return;
        }
        // Find the site from available sites or fetch it
        const site = availableSites.find((s) => s.id === siteId);
        if (site) {
            // Don't set siteData from list - it lacks workItems and versions
            setSiteForm({
                siteName: site.siteName || "",
                address: site.address || "",
                suburb: site.suburb || "",
                state: site.state || "",
                postcode: site.postcode || "",
                buildingType: site.buildingTypes?.[0] || "",
                tenancyName: site.tenancyName || "",
                tenancyNumber: site.tenancyNumber || "",
                landlord: site.landlord || "",
            });
            // Update the job with the new siteId
            if (jobId) {
                try {
                    await updateJob({ siteId });
                    toast.success("Site updated successfully!", 3000);
                }
                catch (err) {
                    console.error("Failed to update job site:", err);
                    toast.error("Failed to update site", 3000);
                }
            }
        }
    };
    // Handle job form changes
    const handleJobChange = (field, value) => {
        setJobForm((prev) => ({ ...prev, [field]: value }));
    };
    const handleJobNameBlur = useCallback(async () => {
        const trimmed = jobNameValue.trim();
        if (!jobId || trimmed === (job?.title ?? ""))
            return;
        try {
            const result = await updateJob({ title: trimmed });
            if (result) {
                await refetch();
                toast.success("Job name updated.", 3000);
            }
        }
        catch (err) {
            console.error("Failed to update job name:", err);
            toast.error("Failed to update job name.", 3000);
            setJobNameValue(job?.title ?? "");
        }
    }, [jobId, jobNameValue, job?.title, updateJob, refetch]);
    const handleQAChange = useCallback((qa) => {
        setJobForm((prev) => ({ ...prev, ...qa }));
    }, []);
    // Handle trade assignment update (sends full payload so PUT api/jobs/trade-assignments/{id} receives all fields)
    const handleTradeAssignmentUpdate = async (assignment, updates) => {
        try {
            const fullPayload = buildFullTradeAssignmentPayload(assignment, updates);
            const result = await updateTradeAssignment(assignment.id, fullPayload);
            if (result) {
                toast.success("Trade assignment updated successfully!", 3000);
            }
            else {
                toast.error("Failed to update trade assignment", 3000);
            }
        }
        catch (err) {
            console.error("Failed to update trade assignment:", err);
            toast.error("Failed to update trade assignment", 3000);
        }
    };
    // Handle creating new trade assignment
    const handleCreateTradeAssignment = async (tradeType, tradespersonId, details) => {
        if (!jobId || !tradespersonId)
            return;
        try {
            const baseData = {
                jobId,
                tradeType,
                tradespersonId,
                isEncumbered: ENCUMBERED_TRADE_TYPES.includes(tradeType),
            };
            const data = {
                ...baseData,
                ...(details?.duration !== undefined && { duration: details.duration }),
                ...(details?.commencementDate && {
                    commencementDate: details.commencementDate,
                }),
                ...(details?.commencementTime && {
                    commencementTime: details.commencementTime,
                }),
                ...(details?.completionDate && {
                    completionDate: details.completionDate,
                }),
                ...(details?.completionTime && {
                    completionTime: details.completionTime,
                }),
                ...(details?.isEncumbered !== undefined && {
                    isEncumbered: details.isEncumbered,
                }),
                ...(details?.didNotShow !== undefined && {
                    didNotShow: details.didNotShow,
                }),
                ...(details?.reason != null && { reason: details.reason }),
                ...(details?.neatJob !== undefined && { neatJob: details.neatJob }),
                ...(details?.reviewProductivity != null && {
                    reviewProductivity: details.reviewProductivity,
                }),
                ...(details?.reviewCleanlinessOfSiteArea != null && {
                    reviewCleanlinessOfSiteArea: details.reviewCleanlinessOfSiteArea,
                }),
                ...(details?.reviewQualityOfWorkmanship != null && {
                    reviewQualityOfWorkmanship: details.reviewQualityOfWorkmanship,
                }),
                ...(details?.reviewPunctuality != null && {
                    reviewPunctuality: details.reviewPunctuality,
                }),
                ...(details?.reviewPresentationAndBehaviour != null && {
                    reviewPresentationAndBehaviour: details.reviewPresentationAndBehaviour,
                }),
                ...(details?.reviewcompliance != null && {
                    reviewcompliance: details.reviewcompliance,
                }),
            };
            await jobsService.createTradeAssignment(data);
            await refetch();
            // Success message for job request sent
            const tradespersonName = allContacts.find((c) => c.id === tradespersonId)?.name || "contractor";
            toast.success(`Job request sent successfully to ${tradespersonName}!`, 3000);
        }
        catch (err) {
            console.error("Failed to create trade assignment:", err);
            // User-friendly error message
            let errorMessage = "Failed to send job request. Please try again.";
            if (err && typeof err === "object" && "response" in err) {
                const axiosError = err;
                if (axiosError.response?.data?.message) {
                    errorMessage = axiosError.response.data.message;
                }
            }
            toast.error(errorMessage, 5000);
            throw err; // Re-throw to allow caller to handle if needed
        }
    };
    // Handle building surveyor change - validate company has a form before setting, persist immediately
    const handleBuildingSurveyorChange = async (surveyorId) => {
        const persistSelection = async (id) => {
            if (!jobId)
                return;
            try {
                await updateJob({
                    buildingSurveyorId: id === null ? null : id,
                });
                await refetch();
            }
            catch {
                toast.error("Failed to save building surveyor selection.", 3000);
                handleJobChange("buildingSurveyorId", job?.buildingSurveyorId ?? "");
            }
        };
        if (!surveyorId) {
            handleJobChange("buildingSurveyorId", surveyorId);
            await persistSelection(null);
            return;
        }
        const surveyor = surveyorContacts.find((c) => c.id === surveyorId);
        const companyName = surveyor?.companyName?.trim();
        if (!companyName) {
            toast.info("Please fill in the company for this building surveyor.", 5000);
            return;
        }
        try {
            const res = await formsService.getFormsByCompanyName(companyName, 1, 20);
            const forms = res.data?.data ?? [];
            const selected = selectCurrentBuildingPermitForm(forms);
            if (selected?.id) {
                handleJobChange("buildingSurveyorId", surveyorId);
                await persistSelection(surveyorId);
                setOpenBuildingPermitForEdit(false);
                setIsBuildingPermitModalOpen(true);
            }
            else {
                toast.info(`No form found for ${companyName}. Please create a form for this company in Forms.`, 5000);
            }
        }
        catch {
            toast.info(`No form found for ${companyName}. Please create a form for this company in Forms.`, 5000);
        }
    };
    // Open document picker for a building permit slot
    const handleSelectDocumentClick = (docType) => {
        setSelectingDocType(docType);
    };
    // Assign an existing site or job document (from documents API) to a building permit slot
    const handleAssignDocument = async (docType, doc) => {
        try {
            await documentsService.updateDocument(doc.id, {
                description: BUILDING_PERMIT_DESCRIPTIONS[docType],
            });
            await refetchSiteDocuments();
            await refetchJobDocumentsFromDocumentsApi?.();
            await refetchQuoteDocuments?.();
            setSelectingDocType(null);
            if (docType === "certificates" &&
                jobId &&
                job &&
                (!job.buildingPermitApproved || !job.buildingPermitApprovedDate)) {
                await updateJob({
                    buildingPermitApproved: true,
                    buildingPermitApprovedDate: nowAUSISO(),
                });
                await refetch();
            }
            if (docType === "cfi_certificate" &&
                jobId &&
                job &&
                (!job.cfiApproved || !job.cfiApprovedDate)) {
                await updateJob({
                    cfiApproved: true,
                    cfiApprovedDate: nowAUSISO(),
                });
                await refetch();
            }
            const label = docType === "cfi_certificate"
                ? "Certificate of Final Inspection"
                : docType.replace(/_/g, " ");
            toast.success(`${label} document assigned successfully!`, 3000);
        }
        catch (err) {
            console.error("Failed to assign document:", err);
            toast.error("Failed to assign document. Please try again.", 5000);
        }
    };
    // Unassign document from building permit slot (keeps file in Documents and Photos)
    const handleRemoveDocument = async (_docType, doc) => {
        try {
            await documentsService.updateDocument(doc.id, { description: "" });
            await refetchSiteDocuments();
            await refetchJobDocumentsFromDocumentsApi?.();
            toast.success("Document removed from building permit.", 3000);
        }
        catch (err) {
            console.error("Failed to remove document:", err);
            toast.error("Failed to remove document. Please try again.", 5000);
        }
    };
    const renderBuildingPermitSlot = (docType, slotLabel) => {
        const docs = buildingPermitDocs[docType] ?? [];
        const fileIcon = (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }), _jsx("polyline", { points: "14 2 14 8 20 8" })] }));
        const removeIcon = (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), _jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }));
        const browseIcon = (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" }), _jsx("polyline", { points: "9 22 9 12 15 12 15 22" })] }));
        const uploadIcon = (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), _jsx("polyline", { points: "17 8 12 3 7 8" }), _jsx("line", { x1: "12", y1: "3", x2: "12", y2: "15" })] }));
        return (_jsxs("div", { className: styles.uploadGroup, children: [_jsx("span", { className: styles.uploadLabel, children: slotLabel }), _jsxs("div", { className: styles.uploadedFilesContainer, children: [docs.map((doc) => {
                            const isJobDocument = "documentType" in doc;
                            return (_jsxs("div", { className: styles.uploadedFile, children: [fileIcon, _jsx("span", { className: styles.uploadedFileName, children: doc.name }), _jsx("div", { className: styles.uploadedFileActions, children: !isJobDocument && (_jsx("button", { className: styles.removeFileBtn, onClick: () => handleRemoveDocument(docType, doc), type: "button", children: removeIcon })) })] }, doc.id));
                        }), _jsxs("button", { className: styles.browseBtn, onClick: () => handleSelectDocumentClick(docType), type: "button", disabled: !siteData?.id, children: [browseIcon, "Select document"] }), docType === "other" && (_jsxs("button", { className: styles.browseBtn, onClick: () => onOpenAddDocument?.({
                                category: "Other",
                                description: "Building Permit - other",
                            }), type: "button", disabled: !jobId, children: [uploadIcon, "Upload"] }))] })] }, docType));
    };
    // Check if site form has any data entered
    const hasSiteFormData = () => {
        return (siteForm.siteName ||
            siteForm.address ||
            siteForm.buildingType ||
            siteForm.tenancyName ||
            siteForm.tenancyNumber ||
            siteForm.landlord ||
            siteForm.state ||
            siteForm.suburb);
    };
    const shouldAutoProgressStatus = job?.status === "not_started" || job?.status === "new_project";
    // Handle save
    const handleSave = async () => {
        try {
            if (jobForm.commencementDate && isWeekend(jobForm.commencementDate)) {
                toast.error("Commencement date cannot fall on a weekend.", 4000);
                return;
            }
            let newSiteId;
            // Check if dates have changed (date or time) - before any async work
            const datesChanged = originalJobDates &&
                (jobForm.durationDays !== originalJobDates.durationDays ||
                    jobForm.commencementDate !== originalJobDates.commencementDate ||
                    jobForm.commencementTime !== originalJobDates.commencementTime ||
                    jobForm.completionDate !== originalJobDates.completionDate ||
                    jobForm.completionTime !== originalJobDates.completionTime);
            // Handle site data only when not in date-change path (avoid PUT empty/stale site)
            if (!datesChanged) {
                if (!job?.siteId && hasSiteFormData()) {
                    const siteCreateData = {
                        siteName: siteForm.siteName || `Site for ${job?.title || "Job"}`,
                        address: siteForm.address,
                        suburb: siteForm.suburb,
                        state: siteForm.state,
                        postcode: siteForm.postcode || undefined,
                        buildingTypes: siteForm.buildingType
                            ? [toEnumBuildingType(siteForm.buildingType)]
                            : [],
                        tenancyName: siteForm.tenancyName,
                        tenancyNumber: siteForm.tenancyNumber,
                        landlord: siteForm.landlord,
                        jobId: jobId,
                    };
                    const newSiteResponse = await sitesService.createSite(siteCreateData);
                    newSiteId = newSiteResponse.data.id;
                    setSiteData(newSiteResponse.data);
                }
            }
            // If dates changed and there are assigned trades or client to notify
            if (datesChanged && jobId) {
                const isInitialEmpty = isInitialEmptyJobDates(originalJobDates);
                if (isInitialEmpty) {
                    // Previous dates were init/empty: save new dates directly, no notification modal
                    const commencementDateTime = toDateTime(jobForm.commencementDate, jobForm.commencementTime);
                    const completionDateTime = toDateTime(jobForm.completionDate, jobForm.completionTime);
                    const result = await updateJob({
                        scope: jobForm.scope || undefined,
                        description: jobForm.description || undefined,
                        durationDays: jobForm.durationDays || undefined,
                        commencementDate: commencementDateTime || undefined,
                        completionDate: completionDateTime || undefined,
                        startTime: jobForm.commencementTime || undefined,
                        endTime: jobForm.completionTime || undefined,
                        buildingSurveyorId: jobForm.buildingSurveyorId || undefined,
                        ...(job?.surveyorStatus === "DECLINED" &&
                            jobForm.buildingSurveyorId !== job?.buildingSurveyorId && {
                            surveyorStatus: "PENDING",
                        }),
                        delayNumber: jobForm.delayNumber || undefined,
                        delayReason: jobForm.delayReason === "Other"
                            ? jobForm.delayReasonOther || undefined
                            : jobForm.delayReason || undefined,
                        siteId: newSiteId,
                        buildingPermitRequested: buildingPermitRequested,
                        buildingPermitApproved: job?.buildingPermitApproved ?? undefined,
                        cfiRequested: cfiRequested,
                        cfiApproved: job?.cfiApproved ?? undefined,
                        jobSetupCorrectly: jobForm.jobSetupCorrectly,
                        prelimsOnTime: jobForm.prelimsOnTime,
                        siteRulesFollowed: jobForm.siteRulesFollowed,
                        finalQA: jobForm.finalQA,
                        ...(shouldAutoProgressStatus && {
                            status: "in_progress",
                        }),
                    });
                    if (result) {
                        toast.success("Changes saved successfully!", 3000);
                        if (newSiteId) {
                            await refetch();
                        }
                    }
                }
                else {
                    setStaffDateChangeData({
                        previousDuration: originalJobDates.durationDays,
                        previousCommencementDate: toDateTime(originalJobDates.commencementDate, originalJobDates.commencementTime),
                        previousCompletionDate: toDateTime(originalJobDates.completionDate, originalJobDates.completionTime),
                        newDuration: jobForm.durationDays,
                        newCommencementDate: toDateTime(jobForm.commencementDate, jobForm.commencementTime),
                        newCompletionDate: toDateTime(jobForm.completionDate, jobForm.completionTime),
                    });
                    setStaffDateChangeReason("");
                    setIsStaffDateChangeModalOpen(true);
                }
            }
            else {
                // No date changes, update everything directly (send full DateTime)
                const commencementDateTime = toDateTime(jobForm.commencementDate, jobForm.commencementTime);
                const completionDateTime = toDateTime(jobForm.completionDate, jobForm.completionTime);
                const result = await updateJob({
                    scope: jobForm.scope || undefined,
                    description: jobForm.description || undefined,
                    durationDays: jobForm.durationDays || undefined,
                    commencementDate: commencementDateTime || undefined,
                    completionDate: completionDateTime || undefined,
                    startTime: jobForm.commencementTime || undefined,
                    endTime: jobForm.completionTime || undefined,
                    buildingSurveyorId: jobForm.buildingSurveyorId || undefined,
                    ...(job?.surveyorStatus === "DECLINED" &&
                        jobForm.buildingSurveyorId !== job?.buildingSurveyorId && {
                        surveyorStatus: "PENDING",
                    }),
                    delayNumber: jobForm.delayNumber || undefined,
                    delayReason: jobForm.delayReason === "Other"
                        ? jobForm.delayReasonOther || undefined
                        : jobForm.delayReason || undefined,
                    siteId: newSiteId,
                    buildingPermitRequested: buildingPermitRequested,
                    buildingPermitApproved: job?.buildingPermitApproved ?? undefined,
                    cfiRequested: cfiRequested,
                    jobSetupCorrectly: jobForm.jobSetupCorrectly,
                    prelimsOnTime: jobForm.prelimsOnTime,
                    siteRulesFollowed: jobForm.siteRulesFollowed,
                    finalQA: jobForm.finalQA,
                    ...(shouldAutoProgressStatus && {
                        status: "in_progress",
                    }),
                });
                if (result) {
                    toast.success("Changes saved successfully!", 3000);
                    if (newSiteId) {
                        await refetch();
                    }
                }
            }
        }
        catch (err) {
            console.error("Save error:", err);
            toast.error("Failed to save changes. Please try again.", 5000);
        }
    };
    // Handle staff date change confirmation - apply dates directly, optionally create notification
    const handleConfirmStaffDateChange = async (reason, sendNotification) => {
        if (!jobId || !staffDateChangeData)
            return;
        try {
            // 1. Apply dates and non-date changes in a single update (no approval needed)
            await updateJob({
                scope: jobForm.scope || undefined,
                description: jobForm.description || undefined,
                durationDays: staffDateChangeData.newDuration,
                commencementDate: staffDateChangeData.newCommencementDate,
                completionDate: staffDateChangeData.newCompletionDate,
                startTime: getTimeFromIso(staffDateChangeData.newCommencementDate) || undefined,
                endTime: getTimeFromIso(staffDateChangeData.newCompletionDate) || undefined,
                buildingSurveyorId: jobForm.buildingSurveyorId || undefined,
                ...(job?.surveyorStatus === "DECLINED" &&
                    jobForm.buildingSurveyorId !== job?.buildingSurveyorId && {
                    surveyorStatus: "PENDING",
                }),
                delayNumber: jobForm.delayNumber || undefined,
                delayReason: jobForm.delayReason === "Other"
                    ? jobForm.delayReasonOther || undefined
                    : jobForm.delayReason || undefined,
                buildingPermitRequested: buildingPermitRequested,
                buildingPermitApproved: job?.buildingPermitApproved ?? undefined,
                cfiRequested: cfiRequested,
                cfiApproved: job?.cfiApproved ?? undefined,
                jobSetupCorrectly: jobForm.jobSetupCorrectly,
                prelimsOnTime: jobForm.prelimsOnTime,
                siteRulesFollowed: jobForm.siteRulesFollowed,
                finalQA: jobForm.finalQA,
                ...(shouldAutoProgressStatus && {
                    status: "in_progress",
                }),
            });
            // 2. Create date change request for notification (client sees Action Required) only when requested
            if (sendNotification) {
                await jobsService.createDateChangeRequest({
                    jobId,
                    requestor: "Staff",
                    requestedById: user?.id ?? "",
                    requestedByName: user?.name ?? "",
                    previousDuration: staffDateChangeData.previousDuration,
                    previousCommencementDate: staffDateChangeData.previousCommencementDate,
                    previousCompletionDate: staffDateChangeData.previousCompletionDate,
                    newDuration: staffDateChangeData.newDuration,
                    newCommencementDate: staffDateChangeData.newCommencementDate,
                    newCompletionDate: staffDateChangeData.newCompletionDate,
                    reason: reason || undefined,
                });
            }
            await refetch();
            toast.success(sendNotification
                ? "Data changes are saved. Notifications have been sent"
                : "Data changes are saved. No notifications have been sent", 5000);
        }
        catch (err) {
            console.error("Failed to save date change:", err);
            toast.error("Failed to save date change. Please try again.", 5000);
        }
        setIsStaffDateChangeModalOpen(false);
        setStaffDateChangeData(null);
        setStaffDateChangeReason("");
    };
    // Handle canceling staff date change - revert form to original dates and times
    const handleCancelStaffDateChange = () => {
        if (originalJobDates) {
            setJobForm((prev) => ({
                ...prev,
                durationDays: originalJobDates.durationDays,
                commencementDate: originalJobDates.commencementDate,
                completionDate: originalJobDates.completionDate,
                commencementTime: originalJobDates.commencementTime,
                completionTime: originalJobDates.completionTime,
            }));
        }
        setIsStaffDateChangeModalOpen(false);
        setStaffDateChangeData(null);
        setStaffDateChangeReason("");
        toast.success("Date change cancelled. Original dates restored.", 3000);
    };
    // Handle date change modal
    const handleViewDetails = () => {
        setIsChangeDateModalOpen(true);
    };
    const handleCloseModal = () => {
        setIsChangeDateModalOpen(false);
    };
    const handleApproveDateChange = async () => {
        const pendingRequest = job?.dateChangeRequests?.find((r) => r.status === "pending");
        if (!pendingRequest)
            return;
        try {
            await jobsService.processDateChangeRequest(pendingRequest.id, {
                action: "approved",
                approvedById: user?.id ?? "",
                approvedByName: user?.name ?? "",
            });
            await refetch();
            toast.success("Date change approved! Dates have been updated.", 3000);
        }
        catch (err) {
            console.error("Failed to approve date change:", err);
            toast.error("Failed to approve date change.", 3000);
        }
        setIsChangeDateModalOpen(false);
    };
    const handleDeclineDateChange = async () => {
        const pendingRequest = job?.dateChangeRequests?.find((r) => r.status === "pending");
        if (!pendingRequest)
            return;
        try {
            await jobsService.processDateChangeRequest(pendingRequest.id, {
                action: "declined",
                approvedById: user?.id ?? "",
                approvedByName: user?.name ?? "",
            });
            await refetch();
            toast.success("Date change declined.", 3000);
        }
        catch (err) {
            console.error("Failed to decline date change:", err);
            toast.error("Failed to decline date change.", 3000);
        }
        setIsChangeDateModalOpen(false);
    };
    const handleCommunicateWithClient = useCallback(async () => {
        const contactId = job?.contactId;
        if (!contactId) {
            toast.error("No client assigned to this job.", 3000);
            return;
        }
        try {
            const thread = await messagesService.getThreadByGeneral(contactId);
            if (!thread) {
                await messagesService.createThread({ contactId, type: "GENERAL" });
            }
            setIsChangeDateModalOpen(false);
            navigate(`${basePath}/messages?tab=messages&contactId=${contactId}`);
        }
        catch (err) {
            const { message } = parseCreateThreadError(err);
            toast.error(message, 3000);
        }
    }, [job?.contactId, basePath, navigate]);
    /** Map trade date change request to DateChangeRequest shape for ChangeDateModal display.
     * API returns datetime in date fields (YYYY-MM-DDTHH:mm:ss); pass as-is. Backward compat: if date-only, concatenate with time. */
    const tradeDateChangeRequestToDisplay = (req) => {
        const toDisplayDate = (dateVal, timeVal) => {
            if (!dateVal)
                return null;
            if (dateVal.includes("T"))
                return dateVal;
            const built = toDateTimeLocal(toDateOnly(dateVal), timeVal ?? "12:00");
            return built || null;
        };
        return {
            id: req.id,
            jobId: req.jobId,
            previousDuration: 0,
            previousCommencementDate: toDisplayDate(req.previousCommencementDate, req.previousCommencementTime),
            previousCompletionDate: toDisplayDate(req.previousCompletionDate, req.previousCompletionTime),
            newDuration: 0,
            newCommencementDate: toDisplayDate(req.newCommencementDate, req.newCommencementTime),
            newCompletionDate: toDisplayDate(req.newCompletionDate, req.newCompletionTime),
            reason: req.reason,
            status: "pending",
            requestor: req.requestor,
            requestedById: req.requestedById,
            requestedByName: req.requestedByName,
            approvedById: req.approvedById,
            approvedByName: req.approvedByName,
            createdAt: req.createdAt,
            approvedAt: req.approvedAt,
            declinedAt: req.declinedAt,
        };
    };
    const handleViewTradeDateChangeDetails = () => {
        setIsTradeDateChangeModalOpen(true);
    };
    const handleCloseTradeDateChangeModal = () => {
        setIsTradeDateChangeModalOpen(false);
    };
    const handleApproveTradeDateChange = async () => {
        if (!pendingTradeDateChangeRequest)
            return;
        try {
            await jobsService.processTradeDateChangeRequest(pendingTradeDateChangeRequest.id, {
                action: "approved",
                approvedById: user?.id ?? "",
                approvedByName: user?.name ?? "",
            });
            if (pendingTradeDateChangeRequest.tradeAssignmentId) {
                const assignment = job?.tradeAssignments?.find((a) => a.id === pendingTradeDateChangeRequest.tradeAssignmentId);
                if (assignment && jobId) {
                    const newCommDateVal = pendingTradeDateChangeRequest.newCommencementDate;
                    const newCompDateVal = pendingTradeDateChangeRequest.newCompletionDate;
                    const newCommDate = toDateOnly(newCommDateVal);
                    const newCompDate = toDateOnly(newCompDateVal);
                    const newCommTime = (getTimeFromIso(newCommDateVal) ||
                        pendingTradeDateChangeRequest.newCommencementTime) ??
                        "12:00";
                    const newCompTime = (getTimeFromIso(newCompDateVal) ||
                        pendingTradeDateChangeRequest.newCompletionTime) ??
                        "12:00";
                    const fullPayload = buildFullTradeAssignmentPayload(assignment, {
                        commencementDate: newCommDate || undefined,
                        commencementTime: newCommTime,
                        completionDate: newCompDate || undefined,
                        completionTime: newCompTime,
                    });
                    await jobsService.updateTradeAssignment(assignment.id, fullPayload);
                }
            }
            await refetchTradeDateChangeRequests?.();
            await refetch();
            toast.success("Trade date change approved. Dates updated.", 3000);
        }
        catch (err) {
            toast.error("Failed to approve trade date change.", 3000);
        }
        setIsTradeDateChangeModalOpen(false);
    };
    const handleDeclineTradeDateChange = async () => {
        if (!pendingTradeDateChangeRequest)
            return;
        try {
            await jobsService.processTradeDateChangeRequest(pendingTradeDateChangeRequest.id, {
                action: "declined",
                approvedById: user?.id ?? "",
                approvedByName: user?.name ?? "",
            });
            await refetchTradeDateChangeRequests?.();
            await refetch();
            toast.success("Trade date change declined.", 3000);
        }
        catch (err) {
            toast.error("Failed to decline trade date change.", 3000);
        }
        setIsTradeDateChangeModalOpen(false);
    };
    const handleCloseBuildingPermitModal = () => {
        setOpenBuildingPermitForEdit(false);
        setIsBuildingPermitModalOpen(false);
    };
    // Handle status change
    const handleStatusChange = async (newStatus) => {
        if (!jobId)
            return;
        try {
            if (newStatus === "completed") {
                const hasAdditionalWorks = job?.isAdditionalWorks ?? job?.isAdditionalWork ?? false;
                if (hasAdditionalWorks) {
                    const res = await additionalWorksService.getAdditionalWorksByJob(jobId);
                    const works = res.data?.additionalWorks ?? [];
                    const hasOpenWorks = works.some((w) => w.status !== "close");
                    if (hasOpenWorks) {
                        await showAlert({
                            title: "Warning",
                            message: "Please first close all additional works variations related to this master job, before closing this job.",
                            type: "warning",
                            confirmText: "OK",
                        });
                        return;
                    }
                }
                const changeStatusDate = getTodayAUS();
                const computedDelay = getDelayDays(jobForm.completionDate, changeStatusDate);
                setJobForm((prev) => ({ ...prev, delayNumber: computedDelay }));
                await jobsService.updateJobStatus(jobId, "completed");
                await updateJob({ delayNumber: computedDelay, status: "completed" });
            }
            else {
                await jobsService.updateJobStatus(jobId, newStatus);
            }
            await refetch();
            toast.success("Job status updated successfully!", 3000);
        }
        catch (err) {
            console.error("Failed to update job status:", err);
            toast.error("Failed to update job status.", 3000);
            await refetch();
        }
    };
    // Handle scheduling complete - update job status to Scheduled
    const handleSchedulingComplete = async () => {
        if (!jobId || job?.status === "scheduled")
            return;
        try {
            await jobsService.updateJobStatus(jobId, "scheduled");
            await refetch();
            toast.success("Job status updated to Scheduled!", 3000);
        }
        catch (err) {
            console.error("Failed to update job status:", err);
            toast.error("Failed to update job status.", 3000);
            await refetch();
        }
    };
    // Handle request building permit
    const handleRequestBuildingPermit = async () => {
        if (!jobId || buildingPermitRequested)
            return;
        try {
            await updateJob({
                buildingPermitRequested: true,
                buildingPermitRequestedDate: nowAUSISO(),
                surveyorStatus: "PENDING",
            });
            setBuildingPermitRequested(true);
            await refetch();
            toast.success("Building permit request submitted!", 3000);
        }
        catch (err) {
            console.error("Failed to request building permit:", err);
            toast.error("Failed to submit building permit request.", 5000);
        }
    };
    // Handle request CFI
    const handleRequestCFI = async () => {
        if (!jobId || cfiRequested)
            return;
        try {
            await updateJob({
                cfiRequested: true,
                cfiRequestedDate: nowAUSISO(),
            });
            setCfiRequested(true);
            await refetch();
            toast.success("CFI request submitted!", 3000);
        }
        catch (err) {
            console.error("Failed to request CFI:", err);
            toast.error("Failed to submit CFI request.", 5000);
        }
    };
    // Get trade assignments grouped
    const encumberedAssignments = job?.tradeAssignments?.filter((a) => a.isEncumbered) || [];
    const defitAssignments = job?.tradeAssignments?.filter((a) => !a.isEncumbered) || [];
    // Get pending date change request
    const pendingDateChangeRequest = job?.dateChangeRequests?.find((r) => r.status === "pending");
    const hasPendingDateChange = !!pendingDateChangeRequest;
    // Pending trade date change request (Contractor-requested — show Action Required in Job Edit)
    const tradeDateChangeRequestsSource = tradeDateChangeRequestsFromProps ?? job?.tradeDateChangeRequests ?? [];
    const pendingTradeDateChangeRequest = tradeDateChangeRequestsSource.find((r) => String(r?.status ?? "").toUpperCase() === "PENDING" &&
        r.requestor === "Contractor");
    // Declined-by-contractor: staff requested date change, contractor declined — show alert to select another tradesperson
    const declinedByContractorTradeDateChangeRequest = tradeDateChangeRequestsSource.find((r) => String(r?.status ?? "").toUpperCase() === "DECLINED" &&
        r.requestor === "Staff");
    // Pending Staff-requested date change — show "Pending Date Change" on card (awaiting contractor approval)
    const pendingStaffTradeDateChangeRequest = tradeDateChangeRequestsSource.find((r) => String(r?.status ?? "").toUpperCase() === "PENDING" &&
        r.requestor === "Staff");
    const handleCommunicateWithContractor = useCallback(async () => {
        if (!pendingTradeDateChangeRequest || !job?.tradeAssignments)
            return;
        const assignment = job.tradeAssignments.find((a) => a.id === pendingTradeDateChangeRequest.jobTradeId);
        const contactId = assignment?.tradespersonId;
        if (!contactId) {
            toast.error("No contractor assigned to this trade.", 3000);
            return;
        }
        try {
            await messagesService.createThread({
                contactId,
                type: "GENERAL",
            });
            setIsTradeDateChangeModalOpen(false);
            navigate(`${basePath}/messages?tab=contractors&contactId=${contactId}`);
        }
        catch (err) {
            const { message } = parseCreateThreadError(err);
            toast.error(message, 3000);
        }
    }, [
        pendingTradeDateChangeRequest,
        job?.tradeAssignments,
        basePath,
        navigate,
    ]);
    const hasBuildingPermitUploaded = job?.buildingPermitApproved === true ||
        jobDocumentsFromProps.some((d) => d.documentType === "building_permit");
    const hasBuildingPermitDraft = Boolean(job?.notes?.includes("Building Permit Application (Draft):"));
    if (loading || siteLoading) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loadingState, children: "Loading job details..." }) }));
    }
    if (error) {
        return (_jsx("div", { className: styles.container, children: _jsxs("div", { className: styles.errorState, children: ["Error: ", error, _jsx("button", { onClick: refetch, className: styles.retryBtn, children: "Retry" })] }) }));
    }
    if (!job) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.errorState, children: "Job not found" }) }));
    }
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.actionBar, children: [_jsxs("div", { className: styles.leftSideActions, children: [job?.quoteId && (_jsx("button", { className: styles.viewDetailButton, onClick: () => window.open(`/admin/estimating/${job.quoteId}/edit`, "_blank"), type: "button", children: "View Work Detail From Estimate" })), !job?.quoteId && job?.siteId && (_jsx("button", { className: styles.viewDetailButton, onClick: () => window.open(`/admin/sites/${job.siteId}/edit`, "_blank"), type: "button", children: "View Work Detail From Site" }))] }), _jsxs("div", { className: styles.actionButtons, children: [_jsxs("div", { className: styles.statusDropdownWrapper, children: [_jsxs("select", { className: styles.statusDropdown, value: job?.status === "not_started"
                                            ? "new_project"
                                            : job?.status || "new_project", onChange: (e) => handleStatusChange(e.target.value), children: [_jsx("option", { value: "new_project", children: "New Project" }), _jsx("option", { value: "in_progress", children: "In Progress" }), _jsx("option", { value: "scheduled", children: "Scheduled" }), _jsx("option", { value: "on_hold", children: "On Hold" }), _jsx("option", { value: "completed", children: "Complete" }), _jsx("option", { value: "invoiced", children: "Invoiced" }), _jsx("option", { value: "cancelled", children: "Cancelled" })] }), _jsx("span", { className: styles.dropdownIcon, children: "keyboard_arrow_down" })] }), _jsx("button", { className: `${styles.actionButton} ${buildingPermitRequested || hasBuildingPermitUploaded
                                    ? styles.actionButtonDisabled
                                    : ""}`, onClick: handleRequestBuildingPermit, disabled: buildingPermitRequested || hasBuildingPermitUploaded, children: job?.buildingPermitApproved
                                    ? "Permit Granted"
                                    : buildingPermitRequested
                                        ? "Permit Requested"
                                        : "Request Building Permit" }), _jsx("button", { className: `${styles.actionButton} ${job?.cfiApproved ? styles.actionButtonFinal : ""}`, onClick: handleRequestCFI, disabled: job?.cfiApproved || false, children: job?.cfiApproved
                                    ? "FINAL"
                                    : job?.cfiRequested
                                        ? "CFI Requested"
                                        : "Request CFI" })] })] }), jobId && (_jsxs("div", { className: styles.dilapidationRow, children: [hasDilapidation && (_jsx(Link, { to: `${basePath}/jobs/${jobId}/dilapidation`, className: styles.dilapidationButton, children: "View Dilapidation Report" })), !hasDilapidation && (_jsx(Link, { to: `${basePath}/jobs/${jobId}/dilapidation/edit`, className: styles.dilapidationButton, children: "Create Dilapidation Report" }))] })), _jsxs("section", { className: styles.section, children: [_jsx("h2", { className: styles.sectionTitle, children: "CONTACT" }), _jsx("div", { className: styles.card, children: _jsxs("div", { className: styles.siteGrid, children: [_jsxs("div", { className: styles.siteColumn, children: [_jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Company" }), _jsxs("div", { className: styles.searchableDropdown, ref: companyDropdownRef, children: [_jsxs("div", { className: styles.searchInputWrapper, children: [_jsx("input", { type: "text", className: styles.searchInput, placeholder: "Type here to search", value: companySearchTerm, onChange: (e) => {
                                                                        setCompanySearchTerm(e.target.value);
                                                                        if (!showCompanyDropdown)
                                                                            setShowCompanyDropdown(true);
                                                                    }, onFocus: () => setShowCompanyDropdown(true), disabled: contactSaving }), selectedCompanyName && (_jsx("button", { type: "button", className: styles.clearButton, onClick: handleClearCompany, title: "Clear selection", children: "\u00D7" }))] }), showCompanyDropdown && (_jsx("div", { className: styles.dropdownList, children: loadingCompanyNames ? (_jsx("div", { className: styles.dropdownLoading, children: "Loading..." })) : companyNames.length > 0 ? (companyNames.slice(0, 50).map((name) => (_jsx("div", { className: styles.dropdownItem, onClick: () => handleCompanySelect(name), children: name }, name)))) : (_jsx("div", { className: styles.dropdownEmpty, children: companySearchTerm
                                                                    ? "No companies found"
                                                                    : "Type to search..." })) }))] })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Shopping centre" }), _jsx("input", { type: "text", className: styles.input, readOnly: true, value: shoppingCentreDisplay.trim()
                                                        ? shoppingCentreDisplay
                                                        : "No shopping centre" })] })] }), _jsx("div", { className: styles.siteColumn, children: _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Contact Name" }), _jsxs("div", { className: styles.searchableDropdown, ref: contactDropdownRef, children: [_jsxs("div", { className: styles.searchInputWrapper, children: [_jsx("input", { type: "text", className: styles.searchInput, placeholder: !selectedCompanyName
                                                                    ? "Select a company first"
                                                                    : "Type here to search", value: contactSearchTerm, onChange: (e) => {
                                                                    setContactSearchTerm(e.target.value);
                                                                    if (!showContactDropdown)
                                                                        setShowContactDropdown(true);
                                                                }, onFocus: () => {
                                                                    if (selectedCompanyName)
                                                                        setShowContactDropdown(true);
                                                                }, disabled: !selectedCompanyName || contactSaving }), selectedContactDisplayName && (_jsx("button", { type: "button", className: styles.clearButton, onClick: handleClearContact, title: "Clear selection", children: "\u00D7" }))] }), showContactDropdown && selectedCompanyName && (_jsx("div", { className: styles.dropdownList, children: filteredCompanyContacts.length > 0 ? (filteredCompanyContacts.map((contact) => (_jsxs("div", { className: styles.dropdownItem, onClick: () => handleContactSelect(contact), children: [_jsx("div", { style: { fontWeight: "500" }, children: formatFullName(contact.firstName, contact.lastName) }), contact.email && (_jsx("div", { style: {
                                                                        fontSize: "0.85em",
                                                                        color: "#666",
                                                                    }, children: contact.email }))] }, contact.contactId)))) : (_jsx("div", { className: styles.dropdownEmpty, children: contactSearchTerm
                                                                ? "No contacts found"
                                                                : "No contacts available" })) }))] })] }) }), _jsx("div", { className: styles.siteColumn, children: _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Job Name" }), _jsx("input", { type: "text", className: styles.input, value: jobNameValue, onChange: (e) => setJobNameValue(e.target.value), onBlur: handleJobNameBlur, placeholder: "Enter job name" })] }) })] }) })] }), _jsxs("section", { className: `${styles.section} ${styles.greyedOutRegion}`, children: [_jsx("h2", { className: styles.sectionTitle, children: "SITE DETAILS" }), _jsxs("div", { className: styles.siteSelect, children: [_jsx("label", { className: styles.label, children: "Select Site" }), _jsxs("select", { className: styles.select, value: selectedSiteId, onChange: (e) => handleSiteSelect(e.target.value), disabled: sitesLoading, children: [_jsx("option", { value: "", children: sitesLoading ? "Loading sites..." : "Select a site" }), availableSites.map((site) => (_jsxs("option", { value: site.id, children: [site.siteName, " - ", formatSiteAddress(site, "No address")] }, site.id)))] })] }), _jsx("div", { className: styles.card, children: _jsxs("div", { className: styles.siteGrid, children: [_jsxs("div", { className: styles.siteColumn, children: [_jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Site Name" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.siteName, readOnly: true })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Address" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.address, readOnly: true })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Suburb" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.suburb, readOnly: true })] })] }), _jsxs("div", { className: styles.siteColumn, children: [_jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "State" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.state, readOnly: true })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Postcode" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.postcode, readOnly: true })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Building Type" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.buildingType, readOnly: true })] })] }), _jsxs("div", { className: styles.siteColumn, children: [_jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Tenancy Name" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.tenancyName, readOnly: true })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Tenancy Number" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.tenancyNumber, readOnly: true })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Landlord" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.landlord, readOnly: true })] })] })] }) })] }), _jsxs("section", { className: styles.section, children: [_jsx("h2", { className: styles.sectionTitle, children: "JOB SCOPE" }), _jsx("div", { className: styles.card, children: _jsxs("div", { className: styles.scopeGrid, children: [_jsxs("div", { className: styles.scopeDescriptionColumn, children: [_jsx("div", { className: styles.scopeColumn, children: _jsxs("div", { className: `${styles.formGroup} form-group-col`, children: [_jsx("label", { className: styles.label, children: "Job Scope" }), _jsx("textarea", { className: styles.textarea, rows: 8, value: jobForm.scope, onChange: (e) => handleJobChange("scope", e.target.value) })] }) }), _jsx("div", { className: styles.descriptionColumn, children: _jsxs("div", { className: `${styles.formGroup} form-group-col`, children: [_jsx("label", { className: styles.label, children: "Job Description" }), _jsx("textarea", { className: styles.textarea, rows: 8, value: jobForm.description, onChange: (e) => handleJobChange("description", e.target.value) })] }) })] }), _jsx("div", { className: styles.checklistColumn, children: _jsxs("div", { className: `${styles.formGroup} form-group-col`, children: [_jsxs("div", { className: checklistStyles.headerRow, children: [_jsx("label", { className: styles.label, children: "Job Checklist" }), canAddChecklistItem && (_jsxs("button", { type: "button", className: checklistStyles.addButton, onClick: () => jobChecklistRef.current?.openAddModal(), children: [_jsx("span", { className: checklistStyles.addButtonIcon, children: "+" }), " ", "Add Item"] }))] }), _jsx(JobChecklist, { ref: jobChecklistRef, jobId: jobId, addButtonExternal: true, items: checklistItems, loading: checklistLoadingFromProps, onRefetch: refetchChecklist })] }) }), _jsx("div", { className: styles.qaColumn, children: _jsxs("div", { className: `${styles.formGroup} form-group-col`, children: [_jsx("label", { className: styles.label, children: "Job QA" }), _jsx(JobQA, { value: {
                                                    jobSetupCorrectly: jobForm.jobSetupCorrectly,
                                                    prelimsOnTime: jobForm.prelimsOnTime,
                                                    siteRulesFollowed: jobForm.siteRulesFollowed,
                                                    finalQA: jobForm.finalQA,
                                                }, onChange: handleQAChange })] }) })] }) })] }), _jsxs("div", { className: styles.twoColumnLayout, children: [_jsxs("section", { className: styles.section, children: [_jsx("h2", { className: styles.sectionTitle, children: "BUILDING PERMIT" }), _jsxs("div", { className: styles.card, children: [job?.surveyorStatus === "DECLINED" && (_jsxs("div", { className: styles.cardTitleRow, children: [_jsx("span", { children: "Building Surveyor" }), _jsx(ActionRequiredBadge, {})] })), _jsx("div", { className: styles.bottomSpacing, children: _jsxs("div", { className: `${styles.formGroup} form-group-col`, children: [_jsx("label", { className: styles.label, children: "Building Surveyor" }), _jsxs("div", { className: styles.buildingSurveyorRow, children: [_jsxs("select", { className: styles.select, value: jobForm.buildingSurveyorId, onChange: (e) => void handleBuildingSurveyorChange(e.target.value), children: [_jsx("option", { value: "", children: "Select Building Surveyor" }), surveyorContacts.map((contact) => (_jsx("option", { value: contact.id, children: getSurveyorDisplayLabel(contact) }, contact.id)))] }), hasBuildingPermitDraft && jobForm.buildingSurveyorId && (_jsx("button", { type: "button", className: styles.editDraftBtn, onClick: () => {
                                                                const surveyor = surveyorContacts.find((c) => c.id === jobForm.buildingSurveyorId);
                                                                const companyName = surveyor?.companyName?.trim();
                                                                if (!companyName) {
                                                                    toast.info("Please fill in the company for this building surveyor.", 5000);
                                                                    return;
                                                                }
                                                                if (resolvedFormId) {
                                                                    setOpenBuildingPermitForEdit(true);
                                                                    setIsBuildingPermitModalOpen(true);
                                                                }
                                                                else {
                                                                    toast.info(`No form found for ${companyName}. Please create a form for this company in Forms.`, 5000);
                                                                }
                                                            }, children: "Edit draft" })), buildingPermitRequested &&
                                                            !hasBuildingPermitDraft &&
                                                            jobId && (_jsx("button", { type: "button", className: styles.viewFormBtn, onClick: () => window.open(`${basePath}/jobs/${jobId}/building-permit-form`, "_blank"), children: "View form" }))] })] }) }), additionalInformationFromNotes && (_jsxs("div", { className: `${styles.formGroup} form-group-col mb-2`, children: [_jsx("label", { className: styles.label, children: "Request for Additional Information" }), _jsx("div", { className: styles.statusNoteDisplay, children: additionalInformationFromNotes })] })), _jsxs("div", { className: `${styles.formGroup} form-group-col`, children: [_jsx("label", { className: styles.label, children: "Attach Documents" }), _jsx("p", { className: styles.helperText, children: "Select from documents already uploaded in the Documents and Photos tab." }), renderBuildingPermitSlot("site_map", "Site Map"), renderBuildingPermitSlot("notice_to_vacate", "Notice to Vacate (SoW)"), renderBuildingPermitSlot("certificates", "Certificates"), renderBuildingPermitSlot("cfi_certificate", "Certificate of Final Inspection"), renderBuildingPermitSlot("other", "Other")] }), _jsx("div", { className: styles.surveyorStatusContainer, children: _jsx("button", { type: "button", className: job?.buildingPermitApproved
                                                ? styles.surveyorStatusAccepted
                                                : buildingPermitRequested
                                                    ? styles.surveyorStatusPending
                                                    : styles.permitStatusNotStarted, children: job?.buildingPermitApproved
                                                ? "Granted"
                                                : buildingPermitRequested
                                                    ? "Pending"
                                                    : "Not Started" }) })] })] }), _jsxs("section", { className: styles.section, children: [_jsx("h2", { className: styles.sectionTitle, children: "JOB DATES" }), _jsxs("div", { className: styles.card, children: [hasPendingDateChange &&
                                        pendingDateChangeRequest?.requestor === "Client" && (_jsxs("div", { className: styles.cardTitleRow, children: [_jsx("span", { children: "Job Dates" }), _jsx(ActionRequiredBadge, { onViewDetails: handleViewDetails })] })), hasPendingDateChange &&
                                        pendingDateChangeRequest?.requestor === "Staff" && (_jsx("div", { className: `${styles.cardTitleRow} ${styles.cardTitleRowJobDates}`, children: _jsx("span", { children: "Job Dates" }) })), hasPendingDateChange &&
                                        pendingDateChangeRequest?.requestor === "Staff" && (_jsx("div", { className: `${styles.cardTitleRow} ${styles.cardTitleRowJobDates}`, children: _jsx("span", { className: styles.pendingClientApproval, children: "Pending Client Approval" }) })), _jsxs("div", { className: `${styles.formGroup} formGroup ${styles.inlineField}`, children: [_jsx("label", { className: `${styles.label} ${styles.inlineLabel}`, children: "Duration" }), _jsxs("div", { className: styles.durationGroup, children: [_jsx("input", { type: "number", className: styles.durationInput, value: jobForm.durationDays, onChange: (e) => handleJobChange("durationDays", parseInt(e.target.value) || 0) }), _jsx("span", { className: styles.durationLabel, children: "(Days)" })] })] }), _jsxs("div", { className: `${styles.formGroup} formGroup ${styles.inlineField}`, children: [_jsx("label", { className: `${styles.label} ${styles.inlineLabel}`, children: "Commencement Date" }), _jsxs("div", { className: styles.dateInputWrapper, children: [_jsx("div", { className: styles.dateInput, children: _jsx(CommencementDateInput, { className: styles.input, value: jobForm.commencementDate, onChange: (value) => handleJobChange("commencementDate", value) }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: styles.input, value: jobForm.commencementTime, onChange: (e) => handleJobChange("commencementTime", e.target.value) }) })] })] }), _jsxs("div", { className: `${styles.formGroup} formGroup ${styles.inlineField}`, children: [_jsx("label", { className: `${styles.label} ${styles.inlineLabel}`, children: "Completion Date" }), _jsxs("div", { className: styles.dateInputWrapper, children: [_jsx("div", { className: styles.dateInput, children: _jsx(DateInput, { className: styles.input, value: jobForm.completionDate, onChange: (value) => handleJobChange("completionDate", value) }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: styles.input, value: jobForm.completionTime, onChange: (e) => handleJobChange("completionTime", e.target.value) }) })] })] }), _jsxs("div", { className: `${styles.formGroup} formGroup ${styles.inlineField}`, children: [_jsx("label", { className: `${styles.label} ${styles.inlineLabel}`, children: "Delay Number" }), _jsxs("div", { className: styles.durationGroup, children: [_jsx("input", { type: "number", className: styles.durationInput, value: jobForm.delayNumber, onChange: (e) => handleJobChange("delayNumber", parseInt(e.target.value) || 0), min: "0" }), _jsx("span", { className: styles.durationLabel, children: "(Days)" })] })] }), _jsxs("div", { className: `${styles.formGroup} formGroup`, children: [_jsx("label", { className: `${styles.label} ${styles.inlineLabel}`, children: "Delay Reason" }), _jsxs("select", { className: styles.select, value: jobForm.delayReason, onChange: (e) => handleJobChange("delayReason", e.target.value), children: [_jsx("option", { value: "", children: "Select" }), _jsx("option", { value: "Flooring", children: "Flooring e.g. screed" }), _jsx("option", { value: "Plastering", children: "Plastering" }), _jsx("option", { value: "Other", children: "Other (please specify)" })] })] }), jobForm.delayReason === "Other" && (_jsxs("div", { className: `${styles.formGroup} formGroup ${styles.inlineField}`, style: { marginTop: "var(--space-sm)" }, children: [_jsx("label", { className: `${styles.label} ${styles.inlineLabel}`, children: "Specify reason" }), _jsx("input", { type: "text", className: styles.input, value: jobForm.delayReasonOther, onChange: (e) => handleJobChange("delayReasonOther", e.target.value), placeholder: "Enter delay reason" })] }))] })] })] }), _jsxs("section", { className: styles.section, children: [_jsx("h2", { className: styles.sectionTitle, children: "TRADE ASSIGNMENT" }), _jsxs("div", { children: [_jsxs("div", { className: styles.tradeTabs, children: [_jsx("button", { className: `${styles.tradeTab} ${tradeTab === "encumbered" ? styles.active : ""}`, onClick: () => setTradeTab("encumbered"), children: "Encumbered Trades" }), _jsx("button", { className: `${styles.tradeTab} ${tradeTab === "defit" ? styles.active : ""}`, onClick: () => setTradeTab("defit"), children: "DEFIT Trades" })] }), _jsx("div", { className: styles.tradeContent, children: tradeTab === "encumbered" ? (_jsx(EncumberedTradesSection, { assignments: encumberedAssignments, onUpdate: handleTradeAssignmentUpdate, onCreate: handleCreateTradeAssignment, jobStatus: job?.status, onRequestChange: (assignment, initialNewDates) => setTradeDateChangeRequestAssignment({
                                        assignment,
                                        initialNewDates,
                                    }), pendingTradeDateChangeRequest: pendingTradeDateChangeRequest, pendingStaffTradeDateChangeRequest: pendingStaffTradeDateChangeRequest, declinedByContractorTradeDateChangeRequest: declinedByContractorTradeDateChangeRequest, onViewTradeDateChangeDetails: handleViewTradeDateChangeDetails })) : (_jsx(DefitTradesSection, { assignments: defitAssignments, onUpdate: handleTradeAssignmentUpdate, onCreate: handleCreateTradeAssignment, jobStatus: job?.status, onRequestChange: (assignment, initialNewDates) => setTradeDateChangeRequestAssignment({
                                        assignment,
                                        initialNewDates,
                                    }), pendingTradeDateChangeRequest: pendingTradeDateChangeRequest, pendingStaffTradeDateChangeRequest: pendingStaffTradeDateChangeRequest, declinedByContractorTradeDateChangeRequest: declinedByContractorTradeDateChangeRequest, onViewTradeDateChangeDetails: handleViewTradeDateChangeDetails })) })] })] }), _jsxs("div", { className: styles.formActions, children: [_jsx("button", { className: styles.saveButton, type: "button", onClick: handleSchedulingComplete, disabled: job?.status === "scheduled" || saving, children: "Scheduling Complete" }), _jsx("button", { className: styles.saveButton, type: "button", onClick: handleSave, disabled: saving, children: saving ? "Saving..." : "Save" })] }), _jsx(TradeDateChangeRequestModal, { isOpen: tradeDateChangeRequestAssignment != null, onClose: () => setTradeDateChangeRequestAssignment(null), jobId: jobId ?? "", assignment: tradeDateChangeRequestAssignment?.assignment ?? null, initialNewDates: tradeDateChangeRequestAssignment?.initialNewDates, jobTradeId: tradeDateChangeRequestAssignment?.assignment?.id, requestor: "Staff", requestedById: user?.id, requestedByName: user?.name, onSuccess: () => {
                    void refetchTradeDateChangeRequests?.();
                    void refetch();
                } }), _jsx(ChangeDateModal, { isOpen: isTradeDateChangeModalOpen, onClose: handleCloseTradeDateChangeModal, onCommunicate: handleCommunicateWithContractor, communicateButtonLabel: "Communicate with Contractor", onApprove: handleApproveTradeDateChange, onDecline: handleDeclineTradeDateChange, dateChangeRequest: pendingTradeDateChangeRequest
                    ? tradeDateChangeRequestToDisplay(pendingTradeDateChangeRequest)
                    : null, formatDatesAsLocal: true }), _jsx(ChangeDateModal, { isOpen: isChangeDateModalOpen, onClose: handleCloseModal, onCommunicate: handleCommunicateWithClient, onApprove: handleApproveDateChange, onDecline: handleDeclineDateChange, dateChangeRequest: pendingDateChangeRequest, formatDatesAsLocal: false }), _jsx(ChangeDateModal, { isOpen: isStaffDateChangeModalOpen, onClose: handleCancelStaffDateChange, onCommunicate: () => { }, onApprove: () => { }, onDecline: handleCancelStaffDateChange, dateChangeRequest: staffDateChangeData
                    ? {
                        id: "",
                        jobId: jobId || "",
                        previousDuration: staffDateChangeData.previousDuration,
                        previousCommencementDate: staffDateChangeData.previousCommencementDate,
                        previousCompletionDate: staffDateChangeData.previousCompletionDate,
                        newDuration: staffDateChangeData.newDuration,
                        newCommencementDate: staffDateChangeData.newCommencementDate,
                        newCompletionDate: staffDateChangeData.newCompletionDate,
                        reason: null,
                        status: "pending",
                        requestedById: null,
                        requestedByName: "Staff",
                        approvedById: null,
                        approvedByName: null,
                        createdAt: new Date().toISOString(),
                        approvedAt: null,
                        declinedAt: null,
                    }
                    : null, isStaffRequest: true, reasonValue: staffDateChangeReason, onReasonChange: setStaffDateChangeReason, onSave: (reason) => handleConfirmStaffDateChange(reason, false), onSaveAndNotify: (reason) => handleConfirmStaffDateChange(reason, true), formatDatesAsLocal: false }), _jsx(BuildingPermitModal, { isOpen: isBuildingPermitModalOpen, onClose: handleCloseBuildingPermitModal, selectedSurveyor: (() => {
                    const contact = surveyorContacts.find((c) => c.id === jobForm.buildingSurveyorId);
                    return contact ? getSurveyorDisplayLabel(contact) : "";
                })(), buildingSurveyorId: jobForm.buildingSurveyorId, formId: resolvedFormId ?? undefined, jobId: jobId, job: job, prefillFromNotes: openBuildingPermitForEdit, onDraftSaved: () => void refetch(), onSubmitSuccess: () => {
                    toast.success("Building permit submitted successfully!", 3000);
                    void refetch();
                } }), _jsx(Modal, { isOpen: selectingDocType !== null, onClose: () => setSelectingDocType(null), title: selectingDocType
                    ? `Select document – ${selectingDocType === "site_map"
                        ? "Site Map"
                        : selectingDocType === "notice_to_vacate"
                            ? "Notice to Vacate (SoW)"
                            : selectingDocType === "certificates"
                                ? "Certificates"
                                : selectingDocType === "cfi_certificate"
                                    ? "Certificate of Final Inspection"
                                    : "Other"}`
                    : "Select document", children: _jsx("div", { className: styles.documentPickerList, children: (() => {
                        // Exclude all docs already assigned to any building permit slot
                        const assignedDocIds = new Set([
                            ...(buildingPermitDocs.site_map ?? []).map((d) => d.id),
                            ...(buildingPermitDocs.notice_to_vacate ?? []).map((d) => d.id),
                            ...(buildingPermitDocs.certificates ?? []).map((d) => d.id),
                            ...(buildingPermitDocs.cfi_certificate ?? []).map((d) => d.id),
                            ...(buildingPermitDocs.other ?? []).map((d) => d.id),
                        ]);
                        const jobPickerItems = jobDocumentsFromProps.map((d) => ({
                            id: d.id,
                            name: d.name || d.fileName,
                            fileName: d.fileName,
                            source: "job",
                        }));
                        const jobDocsApiPickerItems = jobDocumentsFromDocumentsApi
                            .filter((d) => !assignedDocIds.has(d.id))
                            .map((d) => ({
                            id: d.id,
                            name: d.name || d.fileName,
                            fileName: d.fileName,
                            source: "job",
                            document: d,
                        }));
                        const sitePickerItems = siteDocumentsFromProps
                            .filter((d) => !assignedDocIds.has(d.id))
                            .map((d) => ({
                            id: d.id,
                            name: d.name || d.fileName,
                            fileName: d.fileName,
                            source: "site",
                            document: d,
                        }));
                        const quotePickerItems = job?.quoteId && quoteDocumentsFromProps.length > 0
                            ? quoteDocumentsFromProps
                                .filter((d) => !assignedDocIds.has(d.id))
                                .map((d) => ({
                                id: d.id,
                                name: d.name || d.fileName,
                                fileName: d.fileName,
                                source: "quote",
                                document: d,
                            }))
                            : [];
                        const finalPickerItems = finalDocuments.map((d) => ({
                            id: d.id,
                            name: d.name || d.fileName || "",
                            fileName: d.fileName || "",
                            source: "final",
                        }));
                        const totalCount = jobPickerItems.length +
                            jobDocsApiPickerItems.length +
                            sitePickerItems.length +
                            quotePickerItems.length +
                            finalPickerItems.length;
                        if (totalCount === 0) {
                            return (_jsx("p", { className: styles.documentPickerEmpty, children: "No documents uploaded yet. Add documents in the Documents and Photos tab first." }));
                        }
                        const renderSection = (title, items) => (_jsxs("div", { className: styles.documentPickerSection, children: [_jsx("h3", { className: styles.documentPickerSectionTitle, children: title }), items.length === 0 ? (_jsx("p", { className: styles.documentPickerSectionEmpty, children: "None" })) : (_jsx("ul", { className: styles.documentPickerUl, children: items.map((item) => (_jsxs("li", { className: styles.documentPickerItem, children: [_jsx("span", { className: styles.documentPickerName, children: item.name || item.fileName }), _jsx("button", { type: "button", className: item.document
                                                    ? styles.documentPickerSelectBtn
                                                    : styles.documentPickerSelectBtnDisabled, disabled: !item.document, title: !item.document
                                                    ? "Add documents in the Documents and Photos tab to assign them here."
                                                    : undefined, onClick: () => selectingDocType &&
                                                    item.document &&
                                                    handleAssignDocument(selectingDocType, item.document), children: "Select" })] }, `${item.source}-${item.id}`))) }))] }, title));
                        return (_jsxs(_Fragment, { children: [renderSection("Job documents", jobPickerItems.length > 0 || jobDocsApiPickerItems.length > 0
                                    ? [...jobPickerItems, ...jobDocsApiPickerItems]
                                    : []), renderSection("Site documents", sitePickerItems), quotePickerItems.length > 0 &&
                                    renderSection("Quote documents", quotePickerItems), renderSection("Final documents", finalPickerItems)] }));
                    })() }) })] }));
};
/** Group assignments by trade type and return the newest assignment per type (by declinedAt ?? updatedAt). */
function pickNewestAssignmentPerType(assignments) {
    const byTradeType = new Map();
    assignments.forEach((a) => {
        const key = canonicalTradeType(a.tradeType);
        if (!key)
            return;
        const list = byTradeType.get(key) ?? [];
        list.push(a);
        byTradeType.set(key, list);
    });
    const result = new Map();
    byTradeType.forEach((list, tradeType) => {
        const sorted = [...list].sort((x, y) => {
            const dateX = x.declinedAt ?? x.updatedAt;
            const dateY = y.declinedAt ?? y.updatedAt;
            return new Date(dateY).getTime() - new Date(dateX).getTime(); // descending: newest first
        });
        result.set(tradeType, sorted[0]);
    });
    return result;
}
const EncumberedTradesSection = ({ assignments, onUpdate, onCreate, jobStatus, onRequestChange, pendingTradeDateChangeRequest, pendingStaffTradeDateChangeRequest, declinedByContractorTradeDateChangeRequest, onViewTradeDateChangeDetails, }) => {
    const assignmentsByType = pickNewestAssignmentPerType(assignments);
    const tradesToShow = ENCUMBERED_TRADE_TYPES.map((type) => ({
        type,
        assignment: assignmentsByType.get(type) || null,
    }));
    return (_jsx("div", { className: styles.tradeList, children: tradesToShow.map(({ type, assignment }) => (_jsx(TradeCard, { tradeType: type, assignment: assignment, onUpdate: onUpdate, onCreate: onCreate, jobStatus: jobStatus, onRequestChange: onRequestChange, pendingTradeDateChangeRequest: pendingTradeDateChangeRequest, pendingStaffTradeDateChangeRequest: pendingStaffTradeDateChangeRequest, declinedByContractorTradeDateChangeRequest: declinedByContractorTradeDateChangeRequest, onViewTradeDateChangeDetails: onViewTradeDateChangeDetails }, type))) }));
};
const DefitTradesSection = ({ assignments, onUpdate, onCreate, jobStatus, onRequestChange, pendingTradeDateChangeRequest, pendingStaffTradeDateChangeRequest, declinedByContractorTradeDateChangeRequest, onViewTradeDateChangeDetails, }) => {
    const assignmentsByType = pickNewestAssignmentPerType(assignments);
    const tradesToShow = DEFIT_TRADE_TYPES.map((type) => ({
        type,
        assignment: assignmentsByType.get(type) ?? null,
    }));
    return (_jsx("div", { className: styles.tradeList, children: tradesToShow.map(({ type, assignment }) => (_jsx(TradeCard, { tradeType: type, assignment: assignment, onUpdate: onUpdate, onCreate: onCreate, jobStatus: jobStatus, onRequestChange: onRequestChange, pendingTradeDateChangeRequest: pendingTradeDateChangeRequest, pendingStaffTradeDateChangeRequest: pendingStaffTradeDateChangeRequest, declinedByContractorTradeDateChangeRequest: declinedByContractorTradeDateChangeRequest, onViewTradeDateChangeDetails: onViewTradeDateChangeDetails }, type))) }));
};
const TradeCard = ({ tradeType, assignment, onUpdate, onCreate, jobStatus, onRequestChange, pendingTradeDateChangeRequest, pendingStaffTradeDateChangeRequest, declinedByContractorTradeDateChangeRequest, onViewTradeDateChangeDetails, }) => {
    const { confirm } = useAlert();
    const toast = useToast();
    const [isSendingRequest, setIsSendingRequest] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const prevAssignmentIdRef = useRef(null);
    const isEncumbered = ENCUMBERED_TRADE_TYPES.includes(tradeType);
    const subcontractorGroup = isEncumbered
        ? "Encumbered Trades"
        : "DEFIT Trades";
    const tradeCategory = TRADE_TYPE_LABELS[tradeType];
    const { allOptions } = useGhlTradeContacts(subcontractorGroup, tradeCategory, null);
    const companyForAssignment = useMemo(() => {
        if (!assignment?.tradespersonId)
            return null;
        const c = allOptions.find((o) => o.id === assignment.tradespersonId);
        if (c?.companyName != null)
            return c.companyName;
        // Contact not found in allOptions (e.g. contact lacks trade_category tag for this trade) -
        // fall back to the company stored on the assignment so the Company dropdown still fills in.
        const assignmentCompany = (assignment?.tradespersonName ?? "").trim();
        return assignmentCompany || NO_COMPANY_SENTINEL;
    }, [allOptions, assignment?.tradespersonId, assignment?.tradespersonName]);
    const fallbackLabelForAssignment = useMemo(() => {
        if (!assignment?.tradespersonId)
            return undefined;
        const contact = allOptions.find((o) => o.id === assignment.tradespersonId);
        return contact?.name ?? assignment?.tradespersonName ?? undefined;
    }, [allOptions, assignment?.tradespersonId, assignment?.tradespersonName]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    useEffect(() => {
        if (companyForAssignment != null) {
            setSelectedCompany(companyForAssignment);
        }
        else if (!assignment?.tradespersonId) {
            setSelectedCompany(null);
        }
    }, [companyForAssignment, assignment?.tradespersonId]);
    const commencementDateOnly = toDateOnly(assignment?.commencementDate);
    const completionDateOnly = toDateOnly(assignment?.completionDate);
    const initialState = {
        tradespersonId: assignment?.tradespersonId || "",
        tradespersonName: assignment?.tradespersonName || "",
        duration: assignment?.duration || 0,
        commencementDate: commencementDateOnly,
        commencementTime: assignment?.commencementTime || "12:00",
        completionDate: completionDateOnly,
        completionTime: assignment?.completionTime || "12:00",
        didNotShow: assignment?.didNotShow ?? null,
        reason: assignment?.reason || "",
        neatJob: assignment?.neatJob ?? null,
        reviewProductivity: assignment?.reviewProductivity ?? null,
        reviewCleanlinessOfSiteArea: assignment?.reviewCleanlinessOfSiteArea ?? null,
        reviewQualityOfWorkmanship: assignment?.reviewQualityOfWorkmanship ?? null,
        reviewPunctuality: assignment?.reviewPunctuality ?? null,
        reviewPresentationAndBehaviour: assignment?.reviewPresentationAndBehaviour ?? null,
        reviewCompliance: assignment?.reviewCompliance ?? null,
    };
    const [localState, setLocalState] = useState(initialState);
    const [originalState, setOriginalState] = useState(assignment ? { ...initialState } : null);
    useEffect(() => {
        const currentAssignmentId = assignment?.id || null;
        const prevAssignmentId = prevAssignmentIdRef.current;
        // If assignment ID changed, it's a different assignment - update everything
        if (currentAssignmentId !== prevAssignmentId) {
            if (assignment) {
                // New assignment or different assignment - date-only, no timezone
                const commDate = toDateOnly(assignment.commencementDate);
                const compDate = toDateOnly(assignment.completionDate);
                const newState = {
                    tradespersonId: assignment.tradespersonId || "",
                    tradespersonName: assignment.tradespersonName || "",
                    duration: assignment.duration || 0,
                    commencementDate: commDate,
                    commencementTime: assignment.commencementTime || "12:00",
                    completionDate: compDate,
                    completionTime: assignment.completionTime || "12:00",
                    didNotShow: assignment.didNotShow ?? null,
                    reason: assignment.reason || "",
                    neatJob: assignment.neatJob ?? null,
                    reviewProductivity: assignment.reviewProductivity ?? null,
                    reviewCleanlinessOfSiteArea: assignment.reviewCleanlinessOfSiteArea ?? null,
                    reviewQualityOfWorkmanship: assignment.reviewQualityOfWorkmanship ?? null,
                    reviewPunctuality: assignment.reviewPunctuality ?? null,
                    reviewPresentationAndBehaviour: assignment.reviewPresentationAndBehaviour ?? null,
                    reviewCompliance: assignment.reviewCompliance ?? null,
                };
                setLocalState(newState);
                setOriginalState({ ...newState });
            }
            else {
                // Assignment removed - reset to empty state
                const emptyState = {
                    tradespersonId: "",
                    tradespersonName: "",
                    duration: 0,
                    commencementDate: "",
                    commencementTime: "12:00",
                    completionDate: "",
                    completionTime: "12:00",
                    didNotShow: null,
                    reason: "",
                    neatJob: null,
                    reviewProductivity: null,
                    reviewCleanlinessOfSiteArea: null,
                    reviewQualityOfWorkmanship: null,
                    reviewPunctuality: null,
                    reviewPresentationAndBehaviour: null,
                    reviewCompliance: null,
                };
                setLocalState(emptyState);
                setOriginalState(null);
            }
            // Update the ref to track the current assignment ID
            prevAssignmentIdRef.current = currentAssignmentId;
            return;
        }
        // Same assignment - only update if values actually changed (date-only, no timezone)
        if (assignment) {
            const newCommDate = toDateOnly(assignment.commencementDate);
            const newCompDate = toDateOnly(assignment.completionDate);
            setLocalState((prev) => {
                // Compare each field and only update changed ones
                const newTradespersonId = assignment.tradespersonId || "";
                const newTradespersonName = assignment.tradespersonName || "";
                const newDuration = assignment.duration || 0;
                const newCommencementDate = newCommDate;
                const newCommencementTime = assignment.commencementTime || "12:00";
                const newCompletionDate = newCompDate;
                const newCompletionTime = assignment.completionTime || "12:00";
                const newDidNotShow = assignment.didNotShow ?? null;
                const newReason = assignment.reason || "";
                const newNeatJob = assignment.neatJob ?? null;
                const newReviewProductivity = assignment.reviewProductivity ?? null;
                const newReviewCleanlinessOfSiteArea = assignment.reviewCleanlinessOfSiteArea ?? null;
                const newReviewQualityOfWorkmanship = assignment.reviewQualityOfWorkmanship ?? null;
                const newReviewPunctuality = assignment.reviewPunctuality ?? null;
                const newReviewPresentationAndBehaviour = assignment.reviewPresentationAndBehaviour ?? null;
                const newReviewCompliance = assignment.reviewCompliance ?? null;
                // Check if any field actually changed
                const hasChanges = prev.tradespersonId !== newTradespersonId ||
                    prev.tradespersonName !== newTradespersonName ||
                    prev.duration !== newDuration ||
                    prev.commencementDate !== newCommencementDate ||
                    prev.commencementTime !== newCommencementTime ||
                    prev.completionDate !== newCompletionDate ||
                    prev.completionTime !== newCompletionTime ||
                    prev.didNotShow !== newDidNotShow ||
                    prev.reason !== newReason ||
                    prev.neatJob !== newNeatJob ||
                    prev.reviewProductivity !== newReviewProductivity ||
                    prev.reviewCleanlinessOfSiteArea !== newReviewCleanlinessOfSiteArea ||
                    prev.reviewQualityOfWorkmanship !== newReviewQualityOfWorkmanship ||
                    prev.reviewPunctuality !== newReviewPunctuality ||
                    prev.reviewPresentationAndBehaviour !==
                        newReviewPresentationAndBehaviour ||
                    prev.reviewCompliance !== newReviewCompliance;
                // Only update if something actually changed
                if (hasChanges) {
                    const updatedState = {
                        tradespersonId: newTradespersonId,
                        tradespersonName: newTradespersonName,
                        duration: newDuration,
                        commencementDate: newCommencementDate,
                        commencementTime: newCommencementTime,
                        completionDate: newCompletionDate,
                        completionTime: newCompletionTime,
                        didNotShow: newDidNotShow,
                        reason: newReason,
                        neatJob: newNeatJob,
                        reviewProductivity: newReviewProductivity,
                        reviewCleanlinessOfSiteArea: newReviewCleanlinessOfSiteArea,
                        reviewQualityOfWorkmanship: newReviewQualityOfWorkmanship,
                        reviewPunctuality: newReviewPunctuality,
                        reviewPresentationAndBehaviour: newReviewPresentationAndBehaviour,
                        reviewCompliance: newReviewCompliance,
                    };
                    // Update original state when assignment data changes from server
                    setOriginalState({ ...updatedState });
                    return updatedState;
                }
                // No changes - preserve current state
                return prev;
            });
        }
    }, [assignment]);
    const handleTradespersonChange = async (tradespersonId, tradespersonName, meta) => {
        const previousTradespersonId = localState.tradespersonId;
        const isDeclined = assignment?.status === "declined";
        if (meta?.fromCompanyChange) {
            setLocalState((prev) => ({ ...prev, tradespersonId: "" }));
            return;
        }
        // Prevent clearing dropdown when there's an existing assignment (unless declined - allow reassignment)
        if (!tradespersonId && assignment?.tradespersonId && !isDeclined) {
            setLocalState((prev) => ({
                ...prev,
                tradespersonId: previousTradespersonId,
            }));
            return;
        }
        // Check if we're changing from one tradesperson to another (not clearing or first assignment)
        const isChangingTradesperson = assignment &&
            assignment.tradespersonId &&
            previousTradespersonId &&
            tradespersonId &&
            tradespersonId !== previousTradespersonId;
        // When assignment was declined by contractor: show confirmation, then create new pending assignment
        if (isDeclined && tradespersonId) {
            const confirmed = await confirm("Are you sure you want to change the assigned Tradesperson?", "Change Tradesperson", { confirmText: "Yes, Proceed", cancelText: "Cancel" });
            if (!confirmed)
                return;
            await onCreate(tradeType, tradespersonId, {
                duration: localState.duration,
                commencementDate: localState.commencementDate || undefined,
                commencementTime: localState.commencementTime,
                completionDate: localState.completionDate || undefined,
                completionTime: localState.completionTime,
                isEncumbered,
            });
            return;
        }
        // If changing tradesperson (and not declined), show confirmation dialog with names
        if (isChangingTradesperson) {
            const resolveDisplayText = async ({ contactId, explicitName, fallbackLabel, }) => {
                const trimmedExplicitName = (explicitName || "").trim();
                if (trimmedExplicitName)
                    return trimmedExplicitName;
                const id = (contactId || "").trim();
                if (!id)
                    return fallbackLabel;
                try {
                    const contactResponse = await contactsService.getContact(id);
                    const contact = contactResponse.data;
                    const name = formatFullName(contact.firstName, contact.lastName).trim();
                    const email = (contact.email || "").trim();
                    return name || email || fallbackLabel;
                }
                catch {
                    return fallbackLabel;
                }
            };
            const currentName = await resolveDisplayText({
                contactId: assignment.tradespersonId,
                fallbackLabel: "Current Tradesperson",
            });
            const newName = await resolveDisplayText({
                contactId: tradespersonId,
                fallbackLabel: "New Tradesperson",
            });
            const confirmed = await confirm(`Are you sure you want to change the assigned Tradesperson from "${currentName}" to "${newName}"?`, "Change Tradesperson", {
                confirmText: "Yes Notify Both Trades",
                cancelText: "Cancel",
            });
            if (!confirmed) {
                if (companyForAssignment != null) {
                    setSelectedCompany(companyForAssignment);
                }
                return;
            }
        }
        setLocalState((prev) => ({
            ...prev,
            tradespersonId,
            tradespersonName: tradespersonName ?? prev.tradespersonName,
        }));
        // Only call API when a tradesperson is selected. Selecting company clears the
        // tradesperson dropdown (to filter contacts) — do not hit trade-assignments API.
        if (assignment && tradespersonId && !isEncumbered) {
            await onUpdate(assignment, {
                tradespersonId,
                tradespersonName: tradespersonName ?? undefined,
                status: "pending",
            });
        }
    };
    const handleFieldChange = async (field, value) => {
        setLocalState((prev) => ({ ...prev, [field]: value }));
        // Duration and date/time: never auto-update API (encumbered or DEFIT).
        // Changes are tracked locally; user clicks "Save Changes" to persist.
        const isDurationOrDateField = field === "duration" ||
            field === "commencementDate" ||
            field === "commencementTime" ||
            field === "completionDate" ||
            field === "completionTime";
        if (isDurationOrDateField)
            return;
        // DEFIT and encumbered: all changes tracked locally, Save Changes persists.
    };
    // Check if there are unsaved changes (encumbered: all fields; DEFIT: duration, dates, didNotShow, reason, neatJob, reviews)
    const hasUnsavedChanges = () => {
        if (!assignment || !originalState)
            return false;
        const durationOrDateChanged = localState.duration !== originalState.duration ||
            localState.commencementDate !== originalState.commencementDate ||
            localState.commencementTime !== originalState.commencementTime ||
            localState.completionDate !== originalState.completionDate ||
            localState.completionTime !== originalState.completionTime;
        if (!isEncumbered) {
            return (durationOrDateChanged ||
                localState.didNotShow !== originalState.didNotShow ||
                localState.reason !== originalState.reason ||
                localState.neatJob !== originalState.neatJob ||
                localState.reviewProductivity !== originalState.reviewProductivity ||
                localState.reviewCleanlinessOfSiteArea !==
                    originalState.reviewCleanlinessOfSiteArea ||
                localState.reviewQualityOfWorkmanship !==
                    originalState.reviewQualityOfWorkmanship ||
                localState.reviewPunctuality !== originalState.reviewPunctuality ||
                localState.reviewPresentationAndBehaviour !==
                    originalState.reviewPresentationAndBehaviour ||
                localState.reviewCompliance !== originalState.reviewCompliance);
        }
        return (durationOrDateChanged ||
            localState.tradespersonId !== originalState.tradespersonId ||
            localState.didNotShow !== originalState.didNotShow ||
            localState.reason !== originalState.reason ||
            localState.neatJob !== originalState.neatJob ||
            localState.reviewProductivity !== originalState.reviewProductivity ||
            localState.reviewCleanlinessOfSiteArea !==
                originalState.reviewCleanlinessOfSiteArea ||
            localState.reviewQualityOfWorkmanship !==
                originalState.reviewQualityOfWorkmanship ||
            localState.reviewPunctuality !== originalState.reviewPunctuality ||
            localState.reviewPresentationAndBehaviour !==
                originalState.reviewPresentationAndBehaviour ||
            localState.reviewCompliance !== originalState.reviewCompliance);
    };
    // Save all pending changes for encumbered trades
    const handleSaveChanges = async () => {
        if (!assignment || !originalState)
            return;
        setIsSaving(true);
        try {
            const updates = {};
            // Only include changed fields
            if (localState.duration !== originalState.duration) {
                updates.duration = localState.duration ?? undefined;
            }
            if (localState.tradespersonId !== originalState.tradespersonId) {
                updates.tradespersonId = localState.tradespersonId || undefined;
                updates.tradespersonName = localState.tradespersonName || undefined;
                updates.status = "pending";
            }
            if (localState.commencementDate !== originalState.commencementDate ||
                localState.commencementTime !== originalState.commencementTime) {
                updates.commencementDate = localState.commencementDate || undefined;
                updates.commencementTime = localState.commencementTime || undefined;
            }
            if (localState.completionDate !== originalState.completionDate ||
                localState.completionTime !== originalState.completionTime) {
                updates.completionDate = localState.completionDate || undefined;
                updates.completionTime = localState.completionTime || undefined;
            }
            if (localState.didNotShow !== originalState.didNotShow) {
                updates.didNotShow = localState.didNotShow;
            }
            if (localState.reason !== originalState.reason) {
                updates.reason = localState.reason || undefined;
            }
            if (localState.neatJob !== originalState.neatJob) {
                updates.neatJob = localState.neatJob;
            }
            if (localState.reviewProductivity !== originalState.reviewProductivity) {
                updates.reviewProductivity = localState.reviewProductivity ?? undefined;
            }
            if (localState.reviewCleanlinessOfSiteArea !==
                originalState.reviewCleanlinessOfSiteArea) {
                updates.reviewCleanlinessOfSiteArea =
                    localState.reviewCleanlinessOfSiteArea ?? undefined;
            }
            if (localState.reviewQualityOfWorkmanship !==
                originalState.reviewQualityOfWorkmanship) {
                updates.reviewQualityOfWorkmanship =
                    localState.reviewQualityOfWorkmanship ?? undefined;
            }
            if (localState.reviewPunctuality !== originalState.reviewPunctuality) {
                updates.reviewPunctuality = localState.reviewPunctuality ?? undefined;
            }
            if (localState.reviewPresentationAndBehaviour !==
                originalState.reviewPresentationAndBehaviour) {
                updates.reviewPresentationAndBehaviour =
                    localState.reviewPresentationAndBehaviour ?? undefined;
            }
            if (localState.reviewCompliance !== originalState.reviewCompliance) {
                updates.reviewcompliance = localState.reviewCompliance ?? undefined;
            }
            // Only call onUpdate if there are actual changes
            if (Object.keys(updates).length > 0) {
                await onUpdate(assignment, updates);
                // Update original state after successful save
                setOriginalState({ ...localState });
            }
        }
        catch (err) {
            // Error handling is done in onUpdate, but we ensure button is re-enabled
            console.error("Error saving changes:", err);
        }
        finally {
            setIsSaving(false);
        }
    };
    const hasChangeRequest = assignment?.status === "change_requested";
    const isDeclined = assignment?.status === "declined";
    const hasPendingTradeDateChange = !!assignment &&
        !!pendingTradeDateChangeRequest &&
        (pendingTradeDateChangeRequest.tradeAssignmentId === assignment.id ||
            pendingTradeDateChangeRequest.jobTradeId === assignment.id);
    const hasDeclinedByContractor = !!assignment &&
        !!declinedByContractorTradeDateChangeRequest &&
        (declinedByContractorTradeDateChangeRequest.tradeAssignmentId ===
            assignment.id ||
            declinedByContractorTradeDateChangeRequest.jobTradeId === assignment.id);
    const hasPendingStaffDateChange = !!assignment &&
        !!pendingStaffTradeDateChangeRequest &&
        (pendingStaffTradeDateChangeRequest.tradeAssignmentId === assignment.id ||
            pendingStaffTradeDateChangeRequest.jobTradeId === assignment.id);
    const handleSendJobRequest = async () => {
        if (assignment)
            return;
        // Validation: Check if tradesperson is selected
        if (!localState.tradespersonId) {
            toast.error("Please select a tradesperson before sending the job request.");
            return;
        }
        // Validation: Check required fields
        const missingFields = [];
        if (!localState.commencementDate) {
            missingFields.push("Commencement Date");
        }
        if (!localState.completionDate) {
            missingFields.push("Completion Date");
        }
        if (!localState.commencementTime) {
            missingFields.push("Commencement Time");
        }
        if (!localState.completionTime) {
            missingFields.push("Completion Time");
        }
        if (missingFields.length > 0) {
            toast.error(`Please fill in all required fields: ${missingFields.join(", ")}`);
            return;
        }
        // Disable button and show loading state
        setIsSendingRequest(true);
        try {
            const { tradespersonId, ...restOfLocalState } = localState;
            await onCreate(tradeType, localState.tradespersonId, {
                ...restOfLocalState,
                duration: localState.duration ?? undefined,
                commencementDate: localState.commencementDate || undefined,
                commencementTime: localState.commencementTime || undefined,
                completionDate: localState.completionDate || undefined,
                completionTime: localState.completionTime || undefined,
                isEncumbered,
                // Map reviewCompliance (localState) to reviewcompliance (API)
                reviewcompliance: localState.reviewCompliance ?? undefined,
            });
        }
        catch (err) {
            // Error handling is done in onCreate, but we ensure button is re-enabled
            console.error("Error sending job request:", err);
        }
        finally {
            setIsSendingRequest(false);
        }
    };
    return (_jsxs("div", { className: styles.tradeCard, children: [_jsxs("h4", { className: styles.tradeCardTitle, children: [TRADE_TYPE_LABELS[tradeType], (hasChangeRequest || isDeclined) && (_jsxs("span", { className: styles.titleWarning, children: [_jsx("span", { className: styles.warningIcon, children: "warning" }), _jsx("span", { className: styles.warningText, children: "Change Tradesperson" })] })), hasPendingTradeDateChange && onViewTradeDateChangeDetails && (_jsx(ActionRequiredBadge, { onViewDetails: onViewTradeDateChangeDetails })), hasDeclinedByContractor && _jsx(ActionRequiredBadge, {}), hasPendingStaffDateChange && (_jsx("span", { className: styles.titleWarning, children: _jsx("span", { className: styles.warningText, children: "Pending Date Change" }) }))] }), hasDeclinedByContractor && (_jsx("div", { className: styles.actionAlert, children: _jsx("div", { className: styles.alertContent, children: _jsx("div", { className: styles.alertText, children: _jsx("p", { className: styles.alertMessage, children: "Select a Contractor available in these dates" }) }) }) })), _jsxs("div", { className: styles.tradeFormGrid, children: [_jsx("div", { className: `${styles.formGroup} form-group-col`, children: _jsx(GhlTradeContactSelect, { subcontractorGroup: subcontractorGroup, tradeCategory: tradeCategory, value: localState.tradespersonId, onChange: handleTradespersonChange, placeholder: "Select...", allowClear: !assignment?.tradespersonId || isDeclined, allowCompanyChange: isDeclined || !isEncumbered, company: !isEncumbered ? selectedCompany : undefined, onCompanyChange: !isEncumbered ? (c) => setSelectedCompany(c ?? null) : undefined, fallbackLabel: fallbackLabelForAssignment }) }), localState.tradespersonId && (_jsxs(_Fragment, { children: [_jsx("div", { className: `${styles.formGroup} form-group-col`, children: !isEncumbered && (_jsxs(_Fragment, { children: [_jsx("label", { className: styles.label, children: "Duration" }), _jsxs("div", { className: styles.durationGroup, children: [_jsx("input", { type: "number", min: 0, className: styles.durationInput, value: localState.duration === undefined ||
                                                        localState.duration === null
                                                        ? ""
                                                        : localState.duration, onChange: (e) => handleFieldChange("duration", e.target.value === ""
                                                        ? 0
                                                        : parseInt(e.target.value, 10) || 0) }), _jsx("span", { className: styles.durationLabel, children: "(Days)" })] })] })) }), _jsxs("div", { className: `${styles.formGroup} form-group-col`, children: [_jsx("label", { className: styles.label, children: "Commencement Date and Time" }), _jsxs("div", { className: styles.dateInputWrapper, children: [_jsx("div", { className: styles.dateInput, children: _jsx(CommencementDateInput, { className: styles.input, value: localState.commencementDate, onChange: (value) => handleFieldChange("commencementDate", value), disableWeekends: false }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: styles.input, value: localState.commencementTime, onChange: (e) => handleFieldChange("commencementTime", e.target.value) }) })] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-col`, children: [_jsx("label", { className: styles.label, children: "Completion Date and Time" }), _jsxs("div", { className: styles.dateInputWrapper, children: [_jsx("div", { className: styles.dateInput, children: _jsx(DateInput, { className: styles.input, value: localState.completionDate, onChange: (value) => handleFieldChange("completionDate", value) }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: styles.input, value: localState.completionTime, onChange: (e) => handleFieldChange("completionTime", e.target.value) }) })] })] }), jobStatus === "completed" && (_jsxs(_Fragment, { children: [_jsxs("div", { className: `${styles.formGroup} formGroup`, children: [_jsx("label", { className: `${styles.label} ${styles.inlineLabel}`, children: "Did Not Show?" }), _jsx("div", { className: styles.optionButtons, children: ["Yes", "No"].map((option) => {
                                                    const isSelected = (option === "Yes" && localState.didNotShow === true) ||
                                                        (option === "No" && localState.didNotShow === false);
                                                    return (_jsx("button", { type: "button", className: `${styles.optionButton} ${isSelected ? styles.optionButtonActive : ""}`, onClick: () => handleFieldChange("didNotShow", option === "Yes"), children: option }, option));
                                                }) })] }), _jsxs("div", { className: `${styles.formGroup} formGroup`, children: [_jsx("label", { className: `${styles.label} ${styles.inlineLabel}`, children: "Reason" }), _jsxs("select", { className: styles.select, value: localState.reason, onChange: (e) => handleFieldChange("reason", e.target.value), children: [_jsx("option", { value: "", children: "Select" }), _jsx("option", { value: "Injury", children: "Injury" }), _jsx("option", { value: "Illness", children: "Illness" }), _jsx("option", { value: "Accident", children: "Accident" }), _jsx("option", { value: "No Reason", children: "No Reason" })] })] }), _jsxs("div", { className: `${styles.formGroup} formGroup`, children: [_jsx("label", { className: `${styles.label} ${styles.inlineLabel}`, children: "Neat Job?" }), _jsx("div", { className: styles.optionButtons, children: ["Yes", "No"].map((option) => {
                                                    const isSelected = (option === "Yes" && localState.neatJob === true) ||
                                                        (option === "No" && localState.neatJob === false);
                                                    return (_jsx("button", { type: "button", className: `${styles.optionButton} ${isSelected ? styles.optionButtonActive : ""}`, onClick: () => handleFieldChange("neatJob", option === "Yes"), children: option }, option));
                                                }) })] }), _jsxs("div", { className: styles.contractorReviewSection, children: [_jsx("h5", { className: styles.contractorReviewTitle, children: "Contractor's Review (Rate each from 1\u20135 stars)" }), _jsxs("div", { className: styles.contractorReviewRow, children: [_jsx("span", { className: styles.contractorReviewLabel, children: "Productivity" }), _jsx(StarRating, { value: localState.reviewProductivity, onChange: (v) => handleFieldChange("reviewProductivity", v) })] }), _jsxs("div", { className: styles.contractorReviewRow, children: [_jsx("span", { className: styles.contractorReviewLabel, children: "Cleanliness of site area" }), _jsx(StarRating, { value: localState.reviewCleanlinessOfSiteArea, onChange: (v) => handleFieldChange("reviewCleanlinessOfSiteArea", v) })] }), _jsxs("div", { className: styles.contractorReviewRow, children: [_jsx("span", { className: styles.contractorReviewLabel, children: "Quality of workmanship" }), _jsx(StarRating, { value: localState.reviewQualityOfWorkmanship, onChange: (v) => handleFieldChange("reviewQualityOfWorkmanship", v) })] }), _jsxs("div", { className: styles.contractorReviewRow, children: [_jsx("span", { className: styles.contractorReviewLabel, children: "Punctuality" }), _jsx(StarRating, { value: localState.reviewPunctuality, onChange: (v) => handleFieldChange("reviewPunctuality", v) })] }), _jsxs("div", { className: styles.contractorReviewRow, children: [_jsx("span", { className: styles.contractorReviewLabel, children: "Presentation & professional behaviour" }), _jsx(StarRating, { value: localState.reviewPresentationAndBehaviour, onChange: (v) => handleFieldChange("reviewPresentationAndBehaviour", v) })] }), _jsxs("div", { className: styles.contractorReviewRow, children: [_jsx("span", { className: styles.contractorReviewLabel, children: "Compliance with OHS requirements" }), _jsx(StarRating, { value: localState.reviewCompliance, onChange: (v) => handleFieldChange("reviewCompliance", v) })] })] })] })), _jsxs("div", { className: styles.tradeActionWrapper, children: [assignment &&
                                        onRequestChange &&
                                        assignment?.status !== "declined" && (_jsx("button", { className: `${styles.tradeActionBtn} ${styles.requestChange}`, type: "button", onClick: () => onRequestChange?.(assignment, {
                                            commencementDate: localState.commencementDate,
                                            commencementTime: localState.commencementTime,
                                            completionDate: localState.completionDate,
                                            completionTime: localState.completionTime,
                                        }), children: "Request Change" })), assignment?.status === "confirmed" && (_jsx("button", { className: `${styles.tradeActionBtn} ${styles.confirmed}`, type: "button", children: "Confirmed" })), assignment?.status === "change_requested" && (_jsx("button", { className: `${styles.tradeActionBtn} ${styles.changeRequested}`, type: "button", children: "Change Requested" })), assignment?.status === "declined" && (_jsx("button", { className: `${styles.tradeActionBtn} ${styles.declined}`, type: "button", children: "Declined" })), assignment?.status === "pending" && (_jsx("button", { className: `${styles.tradeActionBtn} ${styles.sendRequest}`, type: "button", children: "Pending" })), !assignment && (_jsx("button", { className: `${styles.tradeActionBtn} ${styles.sendRequest}`, type: "button", disabled: !localState.tradespersonId || isSendingRequest, onClick: handleSendJobRequest, children: isSendingRequest ? "Sending..." : "Send Job Request" })), hasUnsavedChanges() && assignment && (_jsx("button", { className: `${styles.tradeActionBtn} ${styles.saveChanges}`, type: "button", disabled: isSaving, onClick: handleSaveChanges, children: isSaving ? "Saving..." : "Save Changes" }))] })] }))] })] }));
};
